import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  QrCode,
  Copy,
  Check,
  Printer,
  Download,
  Store,
  CalendarDays,
  Ticket,
  Gift,
  ChevronDown,
} from 'lucide-react'
import { api } from '../../lib/api'
import { deschideFisier, downloadFile } from '../../lib/downloadFile'
import { SamplingRapoarte } from '../../components/marketing/SamplingRapoarte'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Field } from '../../components/ui/Field'
import { PasswordField } from '../../components/ui/PasswordField'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { TabKituri } from '../../components/marketing/SamplingKituri'
import { SamplingWizard } from '../../components/marketing/SamplingWizard'
import { DocumenteContainer } from '../../components/marketing/DocumenteContainer'
import { FiltreTip, type TipSampling } from '../../components/marketing/FiltreTipSampling'
import { useDebounced } from '../../hooks/useDebounced'
import { DateRangeField, DateQuickButtons } from '../../components/ui/DateField'
import { ModalImportContacte } from '../../components/marketing/ModalImportContacte'
import { TombolaExtrageri } from '../../components/marketing/TombolaExtrageri'
import { TabTransferuri } from '../../components/marketing/SamplingTransferuri'
import {
  BON_LABEL,
  BonuriEsuate,
  EMAIL_LABEL,
  ModalBon,
} from '../../components/marketing/predariComune'

/**
 * Sampling: acțiunile, produsele lor și predările rezultate.
 *
 * Ecranul e construit în jurul unui singur gest: deschizi acțiunea, alegi ce se dă, iei linkul
 * QR și pleci pe teren. Predările apar apoi singure, pe măsură ce oamenii semnează.
 */

interface Actiune {
  /** Activitatea de care ține — de aici se ajunge la pagina ei. */
  campanieId: number | null
  id: number
  denumire: string
  tip: string
  slug: string
  locatie: string | null
  dataStart: string | null
  dataFinal: string | null
  modBon: string
  /** Acțiune de colectare: se strâng contacte, nu se predă nicio mostră. */
  faraMostre: boolean
  /** Textul de deasupra formularului public. `null` = cel implicit al tipului. */
  textIntro: string | null
  textRegulament: string | null
  urlPolitica: string | null
  urlRegulament: string | null
  /** Cadoul, oprit temporar: butonul dispare din formular, lista de produse rămâne. */
  cadouOprit: boolean
  predareLaSemnatura: boolean
  afisDataUri: string | null
  /** Condiția în care se primește cadoul, la tombolă. */
  textCadou: string | null
  activa: boolean
  inchisaLa: string | null
  bonEaDocNo: string | null
  kitId: number | null
  agentUserId: number | null
  agentName: string | null
  /** Containerul de care ține. `null` la acțiunile pornite pe cont propriu. */
  campanie: { id: number; cod: string; titlu: string } | null
  nrPredari: number
  nrProduse: number
  nrProduseActive: number
}

interface ProdusActiune {
  id: number
  productId: number
  sku: string
  denumire: string
  cantitate: number
  activ: boolean
  /** `cadou` = Cadoul Bonus, pentru Story. `premiu` = ce primeste castigatorul tombolei. */
  rol: 'cadou' | 'premiu'
}

interface DetaliuActiune extends Omit<Actiune, 'nrPredari' | 'nrProduse' | 'nrProduseActive'> {
  pharmaClientId: number | null
  pharmaClientName: string | null
  branchId: number | null
  branchLabel: string | null
  /** Containerul din care face parte acțiunea. `null` doar la cele orfane. */
  container: { id: number; cod: string; titlu: string; tip: string } | null
  produse: ProdusActiune[]
}

interface ProdusCatalog {
  id: number
  sku: string
  name: string
  stock: string | null
}

interface Predare {
  id: number
  nrPv: string
  createdAt: string
  statusEmail: string
  statusBon: string
  emailEroare: string | null
  bonEaDocNo: string | null
  bonStornatLa: string | null
  anulataLa: string | null
  /** Ce a scris omul în formular, la „Observații (opțional)”. */
  observatii: string | null
  contact: { nume: string; prenume: string; email: string | null; telefon: string | null }
  actiune: {
    id: number
    denumire: string
    /** Containerul de care ține. `null` la acțiunile pornite pe cont propriu. */
    campanie: { id: number; cod: string; titlu: string } | null
  }
  linii: { denumire: string; cantitate: number; um: string }[]
}

const TIP_LABEL: Record<string, string> = {
  farmacie: 'Farmacie',
  eveniment: 'Eveniment',
  tombola: 'Tombolă',
  promo: 'Promoțional',
}
const MOD_BON_LABEL: Record<string, string> = {
  per_pv: 'Un bon per PV',
  per_actiune: 'Un bon la închiderea acțiunii',
}
const TAB_LABEL: Record<string, string> = {
  actiuni: 'Acțiuni',
  predari: 'Predări',
  contacte: 'Contacte',
  kituri: 'Kituri',
  transferuri: 'Transferuri',
  texte: 'Texte juridice',
  rapoarte: 'Rapoarte',
  setari: 'Setări',
}

export function SamplingPage() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('marketing.sampling:write')
  const canTexte = permissions.includes('marketing.sampling:texte')
  const canGdpr = permissions.includes('marketing.sampling:gdpr')
  const canSterge = permissions.includes('marketing.sampling:delete')
  /*
    Setarile modulului sunt ale adminilor. Alexandru: „vreau ca la tab-ul Setari sa aiba acces doar
    adminii". Acolo stau gestiunea din EA, expeditorul mailurilor, credentialele random.org si
    parola datelor de contact — lucruri care privesc toata lumea, nu lucrul de zi cu zi.

    Ascunderea tab-ului NU e protectia: aia e pe rute, in `sampling.routes.ts`. Aici doar ca sa nu
    apara un buton care raspunde cu 403.
  */
  const eAdmin = useAuthStore((s) => s.user?.role) === 'admin'

  const [tab, setTab] = useState<
    'actiuni' | 'predari' | 'contacte' | 'kituri' | 'transferuri' | 'texte' | 'rapoarte' | 'setari'
  >('actiuni')
  const [stare, setStare] = useState<'toate' | 'active' | 'inchise'>('active')
  const [tipActiuni, setTipActiuni] = useState<TipSampling>('toate')
  const [cauta, setCauta] = useState('')
  const [dataDe, setDataDe] = useState('')
  const [dataPana, setDataPana] = useState('')
  const [wizardTip, setWizardTip] = useState<'farmacie' | 'eveniment' | 'tombola' | 'promo' | null>(
    null,
  )
  /*
    `?actiune=<id>` deschide direct acțiunea.

    Din fila Teren a unui eveniment se vine cu o acțiune anume în minte — codul QR e acolo. Fără
    parametrul ăsta, linkul ateriza în listă și omul o căuta din nou, în ziua evenimentului.
  */
  const [parametri, setParametri] = useSearchParams()
  const navigate = useNavigate()
  const [detaliuId, setDetaliuId] = useState<number | null>(() => {
    const id = Number(parametri.get('actiune'))
    return Number.isFinite(id) && id > 0 ? id : null
  })
  const [bonPredareId, setBonPredareId] = useState<number | null>(null)

  /*
    Anularea unei predari. Nu sterge nimic — PV-ul ramane document justificativ — dar o scoate
    din evidenta activa. La tombole e si singura cale de a scoate o inscriere de test din
    numaratoarea „o singura inscriere de persoana".
  */
  /*
    Bonul stornat in EA. CRM-ul nu verifica nimic — retine ca cineva a declarat-o, ca sa se poata
    anula predarea. Fara ea, o predare cu bon emis ramanea blocata pe veci: stergerea din Expert
    nu ajunge niciodata aici.
  */
  const bonStornat = useMutation({
    mutationFn: async (id: number) => api.post(`/marketing/sampling/predari/${id}/bon-stornat`, {}),
    onSuccess: () => {
      toast.success('Bon marcat ca stornat. Acum poți anula predarea.')
      void queryClient.invalidateQueries({ queryKey: ['sampling'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  const anuleaza = useMutation({
    mutationFn: async (v: { id: number; motiv: string }) =>
      api.post(`/marketing/sampling/predari/${v.id}/anuleaza`, { motiv: v.motiv }),
    onSuccess: () => {
      toast.success('Predare anulată')
      void queryClient.invalidateQueries({ queryKey: ['sampling'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })
  // Filtrul cel mai cerut: „arată-mi doar tombolele". O predare de mostre și o înscriere la
  // tombolă sunt lucruri diferite, iar amestecate în aceeași listă nu se poate lucra cu ele.
  const [tipPredari, setTipPredari] = useState<TipSampling>('toate')
  const [actiunePredari, setActiunePredari] = useState<number | null>(null)

  const actiuni = useQuery({
    queryKey: ['sampling', 'actiuni', stare, tipActiuni, cauta, dataDe, dataPana],
    queryFn: async () => {
      const r = await api.get<Actiune[]>('/marketing/sampling/actiuni', {
        params: {
          stare,
          tip: tipActiuni === 'toate' ? undefined : tipActiuni,
          q: cauta.trim() || undefined,
          dataDe: dataDe || undefined,
          dataPana: dataPana || undefined,
        },
      })
      return r.data
    },
  })

  /*
    Filtrele de stare si cautarea libera.

    Partea de server exista de mult — ruta `/predari` accepta `statusEmail`, `statusBon` si `q`,
    toate validate —, dar interfata nu le-a trimis niciodata. Alexandru, 29.09.2026: „de adaugat
    filtrare dupa status email, bon ea". Cautarea vine la pachet, fiindca e acelasi parametru si
    scuteste derulatul dupa un nume.
  */
  const [statusEmailPredari, setStatusEmailPredari] = useState('toate')
  const [statusBonPredari, setStatusBonPredari] = useState('toate')
  const [cautarePredari, setCautarePredari] = useState('')
  const cautareAplicata = useDebounced(cautarePredari, 300)

  const predari = useQuery({
    queryKey: [
      'sampling',
      'predari',
      tipPredari,
      actiunePredari,
      statusEmailPredari,
      statusBonPredari,
      cautareAplicata,
    ],
    queryFn: async () => {
      const r = await api.get<Predare[]>('/marketing/sampling/predari', {
        params: {
          tipActiune: tipPredari === 'toate' ? undefined : tipPredari,
          actiuneId: actiunePredari ?? undefined,
          statusEmail: statusEmailPredari === 'toate' ? undefined : statusEmailPredari,
          statusBon: statusBonPredari === 'toate' ? undefined : statusBonPredari,
          q: cautareAplicata.trim() || undefined,
        },
      })
      return r.data
    },
    enabled: tab === 'predari',
  })

  const reporneste = useMutation({
    mutationFn: async (id: number) => {
      const r = await api.post<{ id: number }>(`/marketing/sampling/actiuni/${id}/reporneste`)
      return r.data
    },
    onSuccess: (a) => {
      toast.success('Acțiune pornită din nou')
      void queryClient.invalidateQueries({ queryKey: ['sampling'] })
      setDetaliuId(a.id)
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  const sterge = useMutation({
    mutationFn: async (id: number) => api.delete(`/marketing/sampling/actiuni/${id}`),
    onSuccess: () => {
      toast.success('Acțiune ștearsă')
      void queryClient.invalidateQueries({ queryKey: ['sampling'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  const inchide = useMutation({
    mutationFn: async (id: number) => api.post(`/marketing/sampling/actiuni/${id}/inchide`),
    onSuccess: () => {
      toast.success('Acțiune închisă')
      void queryClient.invalidateQueries({ queryKey: ['sampling'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  return (
    <div className="space-y-4">
      {/*
        Bara de tab-uri ramane sus la derulare. Ecranele de sampling sunt lungi — lista de
        produse, predarile, textele juridice — iar pana acum, ca sa treci de la Predari la
        Actiuni, trebuia derulat inapoi pana in cap.
      */}
      <div className="sticky top-0 z-20 -mx-3 flex flex-wrap items-center justify-between gap-2 bg-b4l-bg-soft px-3 py-2">
        <div className="flex gap-1 overflow-x-auto rounded-lg bg-b4l-bg p-1">
          {(
            [
              'actiuni',
              'predari',
              'contacte',
              'kituri',
              'transferuri',
              'texte',
              'rapoarte',
              ...(eAdmin ? (['setari'] as const) : []),
            ] as const
          ).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`rounded-md px-3 py-1.5 text-sm ${
                tab === t ? 'bg-white font-medium text-b4l-text shadow-sm' : 'text-b4l-text-muted'
              }`}
            >
              {TAB_LABEL[t]}
            </button>
          ))}
        </div>
      </div>

      {tab === 'actiuni' && (
        <>
          {canWrite && (
            <Card>
              <div className="p-4">
                <h2 className="text-base font-medium text-b4l-text">Pornește o acțiune</h2>
                <p className="mt-0.5 text-sm text-b4l-text-muted">
                  Alegi contextul, apoi ce se dă. Codul QR se generează la final.
                </p>
                <div className="mt-3 grid gap-2 sm:grid-cols-3">
                  <ButonContext
                    icon={<Store size={18} />}
                    titlu="Farmacie"
                    detaliu="Vizită la un punct de lucru"
                    onClick={() => setWizardTip('farmacie')}
                  />
                  <ButonContext
                    icon={<CalendarDays size={18} />}
                    titlu="Eveniment"
                    detaliu="Târguri, congrese, standuri"
                    onClick={() => setWizardTip('eveniment')}
                  />
                  <ButonContext
                    icon={<Ticket size={18} />}
                    titlu="Tombolă"
                    detaliu="Doar înscrieri, fără mostre"
                    onClick={() => setWizardTip('tombola')}
                  />
                  {/*
                    Varianta ceruta de Victor: „avem periodic influenceri sau persoane care ne cer
                    produse si atunci vrem sa generam un cod qr permanent".

                    Codul e permanent fiindca actiunea n-are data de final — nu e o proprietate a
                    codului, ci a actiunii. La fel ca celelalte tipuri, doar ca aici nu se inchide
                    dupa un eveniment.
                  */}
                  <ButonContext
                    icon={<Gift size={18} />}
                    titlu="Promoțional"
                    detaliu="Influenceri și predări directe, cod permanent"
                    onClick={() => setWizardTip('promo')}
                  />
                </div>
              </div>
            </Card>
          )}

          <Card className="space-y-3">
            <DateQuickButtons
              fromValue={dataDe}
              toValue={dataPana}
              onChange={(de, pana) => {
                setDataDe(de)
                setDataPana(pana)
              }}
            />
            <div className="flex flex-wrap items-end gap-3">
              <DateRangeField
                fromValue={dataDe}
                toValue={dataPana}
                onChange={(de, pana) => {
                  setDataDe(de)
                  setDataPana(pana)
                }}
              />
              <div className="min-w-[240px] flex-1">
                <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Caută</label>
                <input
                  value={cauta}
                  onChange={(e) => setCauta(e.target.value)}
                  placeholder="Client, punct de lucru sau eveniment"
                  className="w-full rounded-lg border border-b4l-border px-3 py-2 text-sm"
                />
              </div>
              {(dataDe || dataPana || cauta || tipActiuni !== 'toate') && (
                <button
                  onClick={() => {
                    setDataDe('')
                    setDataPana('')
                    setCauta('')
                    setTipActiuni('toate')
                  }}
                  className="pb-2 text-xs text-b4l-text-muted hover:underline"
                >
                  Șterge filtrele
                </button>
              )}
            </div>
            <FiltreTip
              valoare={tipActiuni}
              onChange={setTipActiuni}
              actiuni={[]}
              actiuneId={null}
              onActiune={() => undefined}
            />
          </Card>

          <div className="flex gap-2">
            {(['active', 'inchise', 'toate'] as const).map((s) => (
              <button
                key={s}
                onClick={() => setStare(s)}
                className={`rounded-full px-3 py-1 text-sm ${
                  stare === s ? 'bg-b4l-blue text-white' : 'bg-b4l-bg text-b4l-text-muted'
                }`}
              >
                {s === 'active' ? 'Active' : s === 'inchise' ? 'Închise' : 'Toate'}
              </button>
            ))}
          </div>

          <Card>
            {actiuni.isLoading ? (
              <p className="p-4 text-sm text-b4l-text-muted">Se încarcă…</p>
            ) : !actiuni.data?.length ? (
              <p className="p-4 text-sm text-b4l-text-muted">
                Nicio acțiune. Creează una și primești un cod QR pentru teren.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase text-b4l-text-dim">
                    <tr>
                      <th className="px-3 py-2">Acțiune</th>
                      <th className="px-3 py-2">Tip</th>
                      <th className="px-3 py-2">Data</th>
                      <th className="px-3 py-2 text-right">Produse</th>
                      <th className="px-3 py-2 text-right">Predări</th>
                      <th className="px-3 py-2">Stare</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {actiuni.data.map((a) => (
                      <tr key={a.id} className="border-t border-b4l-border">
                        <td className="px-3 py-2">
                          <button
                            onClick={() => setDetaliuId(a.id)}
                            className="font-medium text-b4l-blue hover:underline"
                          >
                            {a.denumire}
                          </button>
                          <div className="text-xs text-b4l-text-muted">
                            {[a.agentName, a.locatie].filter(Boolean).join(' · ') || '—'}
                            {/*
                              Codul QR, ca sa se poata deosebi doua acțiuni cu aceeași denumire
                              — „Pornește din nou" produce exact așa ceva, în aceeași zi.
                            */}
                            <span className="ml-2 font-mono text-b4l-text-dim">{a.slug}</span>
                          </div>
                          {/*
                            De ce activitate tine, CU NUMELE ei. Ecranul asta a ramas cel GENERAL
                            — toate actiunile firmei — deci trebuie sa spuna pentru ce eveniment
                            s-a facut fiecare, si sa duca acolo.

                            Pana pe 29.09.2026 scria doar „vezi activitatea →": un link care
                            raspundea la intrebare numai dupa ce il apasai. Alexandru a cautat
                            „Farmacia Tei Craiova" printre evenimente si n-a gasit-o — statea
                            intr-un program trimestrial, iar randul stia asta si tacea.
                          */}
                          {a.campanieId != null && (
                            <Link
                              to={`/marketing/evenimente/${a.campanieId}`}
                              className="text-xs text-b4l-blue-dark hover:underline"
                            >
                              {a.campanie ? (
                                <>
                                  <span className="font-mono">{a.campanie.cod}</span>{' '}
                                  {a.campanie.titlu} →
                                </>
                              ) : (
                                'vezi activitatea →'
                              )}
                            </Link>
                          )}
                        </td>
                        <td className="px-3 py-2">{TIP_LABEL[a.tip] ?? a.tip}</td>
                        <td className="px-3 py-2 text-b4l-text-muted">
                          {perioada(a.dataStart, a.dataFinal)}
                        </td>
                        <td className="px-3 py-2 text-right">{a.nrProduse}</td>
                        <td className="px-3 py-2 text-right">{a.nrPredari}</td>
                        <td className="px-3 py-2">
                          {a.inchisaLa ? (
                            <span className="rounded-full bg-b4l-bg px-2 py-0.5 text-xs">
                              Închisă
                            </span>
                          ) : !a.activa ? (
                            <span className="rounded-full bg-b4l-bg px-2 py-0.5 text-xs">
                              Oprită
                            </span>
                          ) : a.nrProduseActive === 0 && a.tip !== 'tombola' ? (
                            /*
                             * „Activă" fără niciun produs bifat e o minciună: codul QR se
                             * deschide și arată „Acțiune indisponibilă". S-a întâmplat la
                             * prima acțiune reală — omul a tipărit afișul și a aflat abia la
                             * stand. Starea trebuie să spună asta aici, nu acolo.
                             *
                             * La TOMBOLĂ nu se aplică: acolo nu se dau mostre, doar se înscriu
                             * oameni, iar serverul scutește explicit tombola de verificarea
                             * asta. Semnalul apărea și pe tombole, cerând să bifezi un produs
                             * unei acțiuni care prin definiție n-are — și speriind degeaba.
                             */
                            <span
                              className="rounded-full bg-b4l-amber-light px-2 py-0.5 text-xs text-b4l-amber-dark"
                              title="Bifează cel puțin un produs, altfel codul QR nu deschide formularul"
                            >
                              Fără produse
                            </span>
                          ) : (
                            <span className="rounded-full bg-b4l-green-light px-2 py-0.5 text-xs text-b4l-green-dark">
                              Activă
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          {/*
                            Reluarea sta pe rand, nu intr-o lista separata deasupra. Doua vederi
                            ale aceleiasi liste faceau sa para ca actiunea „se muta" de sus in
                            jos, si nu se stia de unde se sterge.
                          */}
                          {/*
                            „Duplica — cod QR nou", ales de Alexandru din cinci variante. Se numea
                            „Porneste din nou", ceea ce suna a reluare a aceleiasi actiuni; pe 30.08
                            a aparut asa o a doua campanie SCAN&WIN, goala, langa cea cu 688 de
                            inscrieri.

                            Greseala n-a venit din neintelegerea faptului ca se creeaza ceva nou, ci
                            din necunoasterea consecintei: codul QR de pe afisele tiparite ramane
                            legat de actiunea veche. Numele o spune acum din doua cuvinte.

                            Doar adminii: oprirea reala e pe ruta.
                          */}
                          {canWrite && eAdmin && sePoateRelua(a) && (
                            <button
                              onClick={() => reporneste.mutate(a.id)}
                              disabled={reporneste.isPending}
                              className="mr-3 text-xs text-b4l-blue hover:underline disabled:opacity-40"
                              title="Face o ACȚIUNE NOUĂ, azi, cu aceeași configurare, dar cu alt cod QR. Afișele tipărite duc mai departe la acțiunea veche. Pentru ziua a doua a aceleiași campanii NU e nevoie: bazele de extragere se închid singure la miezul nopții."
                            >
                              Duplică — cod QR nou
                            </button>
                          )}
                          {/*
                            Inchiderea, doar la admini. Alexandru: „inchiderea actiunilor vreau sa
                            se poata face doar de admini". E singurul buton de aici care nu se
                            poate desface, si sta lipit de „Porneste din nou".
                          */}
                          {canWrite && eAdmin && !a.inchisaLa && (
                            <button
                              onClick={() => {
                                if (
                                  confirm(`Închizi „${a.denumire}”? Codul QR nu va mai funcționa.`)
                                )
                                  inchide.mutate(a.id)
                              }}
                              className="text-xs text-b4l-text-muted hover:text-b4l-red"
                            >
                              Închide
                            </button>
                          )}
                          {/*
                            Ștergerea apare doar la acțiunile care n-au produs niciun document.
                            Cele cu predări se închid: procesele-verbale emise rămân, iar
                            acțiunea care le explică trebuie să rămână cu ele.
                          */}
                          {canSterge && a.nrPredari === 0 && (
                            <button
                              onClick={() => {
                                if (
                                  confirm(
                                    `Ștergi definitiv „${a.denumire}”? Nu are nicio predare, deci nu se pierde niciun document.`,
                                  )
                                )
                                  sterge.mutate(a.id)
                              }}
                              className="ml-3 text-xs text-b4l-red hover:underline"
                            >
                              Șterge
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      {tab === 'predari' && (
        <>
          <FiltreTip
            valoare={tipPredari}
            onChange={(v) => {
              setTipPredari(v)
              setActiunePredari(null)
            }}
            actiuni={actiuni.data ?? []}
            actiuneId={actiunePredari}
            onActiune={setActiunePredari}
          />
          <FiltrePredari
            statusEmail={statusEmailPredari}
            onStatusEmail={setStatusEmailPredari}
            statusBon={statusBonPredari}
            onStatusBon={setStatusBonPredari}
            cauta={cautarePredari}
            onCauta={setCautarePredari}
            gasite={predari.data?.length ?? 0}
          />
          <BonuriEsuate
            actiuneId={actiunePredari}
            esuateInLista={(predari.data ?? []).filter((p) => p.statusBon === 'esuat').length}
            canWrite={canWrite}
          />
          <Card>
            {predari.isLoading ? (
              <p className="p-4 text-sm text-b4l-text-muted">Se încarcă…</p>
            ) : !predari.data?.length ? (
              <p className="p-4 text-sm text-b4l-text-muted">Nicio predare încă.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase text-b4l-text-dim">
                    <tr>
                      <th className="px-3 py-2">PV</th>
                      <th className="px-3 py-2">Data</th>
                      <th className="px-3 py-2">Primitor</th>
                      <th className="px-3 py-2">Acțiune</th>
                      <th className="px-3 py-2">Produse</th>
                      <th className="px-3 py-2">Email</th>
                      <th className="px-3 py-2">Bon EA</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {predari.data.map((p) => (
                      <tr key={p.id} className="border-t border-b4l-border">
                        <td className="px-3 py-2 font-mono text-xs">
                          {p.nrPv}
                          {p.anulataLa && <span className="ml-1 text-b4l-red">(anulat)</span>}
                        </td>
                        {/*
                          Ora, pana la secunda. La o tombola ordinea inscrierilor E ordinea
                          numerelor extrase — o coloana care arata doar ziua nu poate departaja
                          doi oameni inscrisi la zece secunde distanta.
                        */}
                        <td className="whitespace-nowrap px-3 py-2 tabular-nums text-b4l-text-muted">
                          {ziOra(p.createdAt)}
                        </td>
                        <td className="px-3 py-2">
                          {p.contact.prenume} {p.contact.nume}
                          <div className="text-xs text-b4l-text-muted">{p.contact.email}</div>
                          {/* „Observatii (optional)" din formularul de la QR (22.10.2026). Doua randuri, restul la hover. */}
                          {p.observatii && (
                            <div
                              className="mt-0.5 line-clamp-2 max-w-xs whitespace-pre-line break-words text-xs text-b4l-text-muted"
                              title={p.observatii}
                            >
                              <span className="font-medium">Obs.:</span> {p.observatii}
                            </div>
                          )}
                        </td>
                        {/*
                          Si containerul, sub denumire. Fara el, „Farmacia Tei Craiova" nu spune
                          din ce program face parte, iar cautarea o ia prin evenimente.
                        */}
                        <td className="px-3 py-2 text-b4l-text-muted">
                          {p.actiune.denumire}
                          {p.actiune.campanie && (
                            <div className="text-xs text-b4l-text-dim">
                              {p.actiune.campanie.cod} · {p.actiune.campanie.titlu}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-b4l-text-muted">
                          {p.linii.map((l) => `${l.denumire} ×${l.cantitate}`).join(', ')}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={
                              p.statusEmail === 'esuat' ? 'text-b4l-red' : 'text-b4l-text-muted'
                            }
                            title={p.emailEroare ?? undefined}
                          >
                            {EMAIL_LABEL[p.statusEmail] ?? p.statusEmail}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-b4l-text-muted">
                          {BON_LABEL[p.statusBon] ?? p.statusBon}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {/*
                            Documentul exista daca s-a predat marfa, nu dupa prefixul numarului.

                            Conditia era `nrPv.startsWith('TB-')`, adica „inscriere la tombola,
                            deci niciun document" — adevarat cand tombola insemna doar inscriere.
                            De cand se predau si Cadoul Bonus, si premiul, o inscriere `TB-` are
                            proces-verbal ca oricare alta. Alexandru: „nu apare PV", chiar dupa ce
                            predase premiul.

                            Marfa scrisa pe inscriere inseamna marfa data — acelasi criteriu ca in
                            vederea de stand.
                          */}
                          {p.linii.length === 0 ? (
                            <span className="text-xs text-b4l-text-dim">—</span>
                          ) : (
                            <>
                              <button
                                onClick={() => {
                                  void downloadFile(
                                    `/marketing/sampling/predari/${p.id}/pdf`,
                                    {},
                                    `${p.nrPv}.pdf`,
                                  ).catch((e: Error) => toast.error(e.message))
                                }}
                                className="text-xs text-b4l-blue hover:underline"
                              >
                                Descarcă PV
                              </button>
                              {/*
                                Vizualizare, langa descarcare. Alexandru le-a cerut pe amandoua:
                                de cele mai multe ori vrei sa VEZI documentul, nu sa-l ai in
                                Downloads. Vizualizatorul browserului are si el buton de salvare,
                                deci cine chiar vrea fisierul il ia de acolo.
                              */}
                              <button
                                onClick={() => {
                                  void deschideFisier(
                                    `/marketing/sampling/predari/${p.id}/pdf`,
                                  ).catch((e: Error) => toast.error(e.message))
                                }}
                                className="ml-2 text-xs text-b4l-blue hover:underline"
                              >
                                Vezi
                              </button>
                              <button
                                onClick={() => setBonPredareId(p.id)}
                                className="ml-2 text-xs text-b4l-text-muted hover:underline"
                              >
                                Bon
                              </button>
                            </>
                          )}
                          {/*
                            Anularea exista pe server de la inceput, dar n-avea niciun buton —
                            deci nu exista pentru nimeni. Se vede la orice predare neanulata:
                            si la tombole, unde e singurul fel de a scoate o inscriere de test
                            din numaratoare.

                            Nu sterge nimic: PV-ul ramane document justificativ. Doar il scoate
                            din evidenta activa, cu motivul scris.
                          */}
                          {/*
                            Deblocarea anularii, cand bonul a fost stornat in Expert. Se vede doar
                            acolo unde chiar blocheaza ceva: bon emis, inca nemarcat, predare
                            neanulata. Textul spune ce declari, nu ce apesi.
                          */}
                          {canWrite && !p.anulataLa && p.bonEaDocNo && !p.bonStornatLa && (
                            <button
                              onClick={() => {
                                if (
                                  window.confirm(
                                    `Confirmi că bonul de consum ${p.bonEaDocNo} a fost stornat sau șters în ExpertAccounts?

CRM-ul nu poate verifica asta; rămâne înregistrat că ai confirmat tu.`,
                                  )
                                ) {
                                  bonStornat.mutate(p.id)
                                }
                              }}
                              className="ml-2 text-xs text-b4l-amber hover:underline"
                              title={`Bon ${p.bonEaDocNo} — deblochează anularea`}
                            >
                              Bon stornat
                            </button>
                          )}
                          {canWrite && !p.anulataLa && (
                            <button
                              onClick={() => {
                                const motiv = window.prompt(
                                  `Anulezi ${p.nrPv}. De ce? (rămâne scris în evidență)`,
                                )
                                if (motiv && motiv.trim().length >= 3) {
                                  anuleaza.mutate({ id: p.id, motiv: motiv.trim() })
                                } else if (motiv !== null) {
                                  toast.error('Scrie un motiv, măcar trei litere.')
                                }
                              }}
                              className="ml-2 text-xs text-b4l-red hover:underline"
                            >
                              Anulează
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      {tab === 'contacte' && (
        <TabContacte canWrite={canWrite} canGdpr={canGdpr} actiuni={actiuni.data ?? []} />
      )}
      {tab === 'kituri' && <TabKituri canWrite={canWrite} />}
      {tab === 'transferuri' && <TabTransferuri canWrite={canWrite} canSterge={canSterge} />}
      {tab === 'texte' && <TabTexte canTexte={canTexte} />}
      {/*
        Rapoartele. Cerute de Adela, iar Victor le-a dat forma: „cate kituri am dat versus cele care
        se anuleaza ca nu au venit dupa ele". Drept de CITIRE, ca restul modulului.
      */}
      {tab === 'rapoarte' && <SamplingRapoarte />}
      {tab === 'setari' && eAdmin && <TabSetari canWrite={canWrite} />}

      {bonPredareId !== null && (
        <ModalBon
          predareId={bonPredareId}
          canWrite={canWrite}
          onClose={() => setBonPredareId(null)}
        />
      )}

      {wizardTip && (
        <SamplingWizard
          tip={wizardTip}
          onClose={() => setWizardTip(null)}
          onCreat={(id) => {
            setWizardTip(null)
            setDetaliuId(id)
          }}
        />
      )}

      {detaliuId !== null && (
        <ModalDetaliu
          id={detaliuId}
          canWrite={canWrite}
          onClose={() => {
            setDetaliuId(null)
            /*
              Te întorci de unde ai venit.

              Din fila Teren a unui eveniment, „Deschide” aducea aici, iar „Închide” te lăsa în
              lista de acțiuni — alt meniu decât cel din care plecaseși, cu evenimentul pierdut
              (Alexandru, 22.09.2026). Adresa de întoarcere vine pe `inapoi`, și numai una internă:
              o valoare care începe cu `//` sau cu un protocol ar scoate omul din CRM.
            */
            const inapoi = parametri.get('inapoi')
            if (inapoi && inapoi.startsWith('/') && !inapoi.startsWith('//')) {
              navigate(inapoi)
              return
            }
            // Parametrul iese din adresă: altfel un refresh ar redeschide la nesfârșit.
            if (parametri.has('actiune')) {
              parametri.delete('actiune')
              setParametri(parametri, { replace: true })
            }
          }}
        />
      )}
    </div>
  )
}

/**
 * Panoul unei actiuni: cod QR, afis, produse, documente.
 *
 * Exportat fiindca se deschide si din fila Teren a unui eveniment, PE LOC. Inainte, „Deschide"
 * era un link catre lista de actiuni, deci te muta in alt meniu si pierdeai evenimentul —
 * Alexandru, 22.09.2026: „cand apas Deschide, ma muta in meniul Actiuni. Nu vrem asta".
 *
 * Panoul a ramas aici, langa lista din care s-a nascut si langa ajutoarele pe care le
 * foloseste (`Pliant`, `mesaj`, `zi`); mutarea lui intr-un fisier propriu ar fi tras dupa ea
 * si jumatate din ele, in dimineata unui eveniment.
 */
export function ModalDetaliu({
  id,
  canWrite,
  onClose,
}: {
  id: number
  canWrite: boolean
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [copiat, setCopiat] = useState(false)

  const detaliu = useQuery({
    queryKey: ['sampling', 'actiune', id],
    queryFn: async () => {
      const r = await api.get<DetaliuActiune>(`/marketing/sampling/actiuni/${id}`)
      return r.data
    },
  })

  const catalog = useQuery({
    queryKey: ['sampling', 'produse'],
    queryFn: async () => {
      const r = await api.get<{ produse: ProdusCatalog[] }>('/marketing/sampling/produse')
      return r.data
    },
  })

  /* Cine poate fi agent pe o acțiune. Aceeași listă pe care o folosește expertul la pornire. */
  const agenti = useQuery({
    queryKey: ['sampling', 'agenti'],
    queryFn: async () =>
      (await api.get<{ id: number; name: string }[]>('/marketing/sampling/agenti')).data,
  })

  const qr = useQuery({
    queryKey: ['sampling', 'qr', id],
    queryFn: async () => {
      const r = await api.get<{ url: string; png: string }>(`/marketing/sampling/actiuni/${id}/qr`)
      return r.data
    },
  })

  /**
   * Redenumirea acțiunii, din chiar titlul ferestrei.
   *
   * Denumirea ajunge pe afiș și pe procesul-verbal, iar la prima acțiune reală a ajuns acolo
   * numele unei persoane. Corectarea trebuia să fie la îndemână, nu într-un alt ecran.
   */
  const redenumeste = useMutation({
    mutationFn: async (denumire: string) =>
      api.patch(`/marketing/sampling/actiuni/${id}`, { denumire }),
    onSuccess: () => {
      toast.success('Denumire schimbată')
      void queryClient.invalidateQueries({ queryKey: ['sampling'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  const schimbaIntro = useMutation({
    mutationFn: async (textIntro: string | null) =>
      api.patch(`/marketing/sampling/actiuni/${id}`, { textIntro }),
    onSuccess: (_d, textIntro) => {
      toast.success(textIntro ? 'Text salvat' : 'Textul a revenit la cel implicit')
      void queryClient.invalidateQueries({ queryKey: ['sampling'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  /*
    Agentul actiunii. Se putea alege DOAR la pornire, ascuns sub „Schimba" in expert, si ramanea
    pe cine a apasat butonul. Adrian Mihai — manager de vanzari — a pornit „Farmacia Tei Craiova"
    pentru Doru Lacatusu si a ramas el agent (Alexandru, 29.09.2026).

    Nu e cosmetica. Agentul actiunii decide trei lucruri: din ce gestiune iese marfa la predare
    (vezi `gestiunePredare`), cine apare pe bonul de consum, si cine vede actiunea in lista — un
    agent de vanzari vede doar ce e al lui. Cu agentul gresit, Doru nici nu gasea actiunea, iar
    mostrele ar fi iesit din gestiunea lui Adrian.
  */
  const schimbaAgent = useMutation({
    mutationFn: async (agentUserId: number | null) =>
      api.patch(`/marketing/sampling/actiuni/${id}`, { agentUserId }),
    onSuccess: () => {
      toast.success('Agent schimbat')
      void queryClient.invalidateQueries({ queryKey: ['sampling'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  /*
    Afisul campaniei. Modelul cerut de Alexandru — campania Nestlé de la Mega Image — incepe cu
    un banner, apoi formularul. Se incarca o data, la pregatirea actiunii.
  */
  const incarcaAfis = useMutation({
    mutationFn: async (fisier: File) => {
      const fd = new FormData()
      fd.append('file', fisier)
      return api.post(`/marketing/sampling/actiuni/${id}/afis`, fd)
    },
    onSuccess: () => {
      toast.success('Afiș încărcat')
      void queryClient.invalidateQueries({ queryKey: ['sampling'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  const stergeAfis = useMutation({
    mutationFn: async () => api.delete(`/marketing/sampling/actiuni/${id}/afis`),
    onSuccess: () => {
      toast.success('Afiș șters')
      void queryClient.invalidateQueries({ queryKey: ['sampling'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  const schimbaRegulament = useMutation({
    mutationFn: async (textRegulament: string | null) =>
      api.patch(`/marketing/sampling/actiuni/${id}`, { textRegulament }),
    onSuccess: (_d, t) => {
      toast.success(t ? 'Regulament salvat' : 'Regulamentul a fost șters de pe acțiune')
      void queryClient.invalidateQueries({ queryKey: ['sampling'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  /*
    Oprirea cadoului, cand s-au terminat produsele. Alexandru, in ziua evenimentului: „nu mai sunt
    produse de distribuit (...) putem dezactiva butonul «Vreau produsul gratuit»?".
  */
  const schimbaCadouOprit = useMutation({
    mutationFn: async (cadouOprit: boolean) =>
      api.patch(`/marketing/sampling/actiuni/${id}`, { cadouOprit }),
    onSuccess: (_d, v) => {
      toast.success(v ? 'Cadoul nu se mai oferă pe formular' : 'Cadoul se oferă din nou')
      void queryClient.invalidateQueries({ queryKey: ['sampling'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  /*
    „Doar colectare de date, fara mostre" — se putea pune DOAR la creare.

    Consecinta, vazuta pe 22.09.2026: actiunea din farmacia Tei fusese creata cu bifa pusa, apoi
    i s-au adaugat patru produse. Formularul cerea semnatura si scria „Ai primit", dar serverul
    nu scria nicio linie de predare si nu trimitea niciun bon — iar bifa nu se mai putea scoate
    de nicaieri. Alexandru: „asta nu gasesc". Nu avea unde.
  */
  const schimbaFaraMostre = useMutation({
    mutationFn: async (faraMostre: boolean) =>
      api.patch(`/marketing/sampling/actiuni/${id}`, { faraMostre }),
    onSuccess: (_d, v) => {
      toast.success(
        v
          ? 'Acțiunea doar colectează date: nu se predă nimic'
          : 'Acțiunea poate preda mostre. Alege produsele, apoi predă-le de la stand',
      )
      void queryClient.invalidateQueries({ queryKey: ['sampling'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  /*
    Scenariul „predarea se face la semnatura".

    Alexandru, 30.09.2026: la evenimentele mici, de 20-30 de oameni, datele se colecteaza SI
    produsele se dau in aceeasi clipa. Al doilea pas inseamna oprirea omului a doua oara — iar
    daca el a plecat deja, procesul-verbal ramane nesemnat. La un eveniment real agentul a semnat
    el, la final, in numele participantilor plecati.

    Ramane bifa, nu regula: calea normala e tot cea din 24.09.2026.
  */
  const schimbaPredareLaSemnatura = useMutation({
    mutationFn: async (predareLaSemnatura: boolean) =>
      api.patch(`/marketing/sampling/actiuni/${id}`, { predareLaSemnatura }),
    onSuccess: (_d, v) => {
      toast.success(
        v
          ? 'Formularul cere semnătura și predă produsele pe loc'
          : 'S-a revenit la predarea de la stand',
      )
      void queryClient.invalidateQueries({ queryKey: ['sampling'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  const schimbaLinkRegulament = useMutation({
    mutationFn: async (urlRegulament: string | null) =>
      api.patch(`/marketing/sampling/actiuni/${id}`, { urlRegulament }),
    onSuccess: (_d, u) => {
      toast.success(u ? 'Link salvat' : 'S-a revenit la textul din CRM')
      void queryClient.invalidateQueries({ queryKey: ['sampling'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  const schimbaPolitica = useMutation({
    mutationFn: async (urlPolitica: string | null) =>
      api.patch(`/marketing/sampling/actiuni/${id}`, { urlPolitica }),
    onSuccess: (_d, u) => {
      toast.success(u ? 'Link salvat' : 'S-a revenit la linkul din Setări')
      void queryClient.invalidateQueries({ queryKey: ['sampling'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  const schimbaCadou = useMutation({
    mutationFn: async (textCadou: string | null) =>
      api.patch(`/marketing/sampling/actiuni/${id}`, { textCadou }),
    onSuccess: () => {
      toast.success('Condiția pentru cadou salvată')
      void queryClient.invalidateQueries({ queryKey: ['sampling'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  const salveazaProduse = useMutation({
    mutationFn: async (v: {
      linii: { productId: number; cantitate: number; activ: boolean }[]
      rol: 'cadou' | 'premiu'
    }) => api.put(`/marketing/sampling/actiuni/${id}/produse`, { linii: v.linii, rol: v.rol }),
    onSuccess: () => {
      toast.success('Produse salvate')
      void queryClient.invalidateQueries({ queryKey: ['sampling'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  /*
    Kiturile, pentru incarcarea premiului dintr-un singur clic.

    Alexandru: „premiul tombolei e un kit, pe care l-am definit la kit-uri". Are dreptate —
    kiturile exista tocmai ca sa nu alegi aceleasi produse de doua ori. Fara asta, ecranul de
    premii cerea sa reconstruiesti de mana ce era deja definit.
  */
  const kituri = useQuery({
    queryKey: ['sampling', 'kituri'],
    queryFn: async () =>
      (
        await api.get<
          {
            id: number
            denumire: string
            activ: boolean
            linii: { productId: number; denumire: string; cantitate: number }[]
          }[]
        >('/marketing/sampling/kituri')
      ).data,
  })

  /** Cautare in lista de produse. Lista tine acum tot raftul de suplimente. */
  const [cautaProdus, setCautaProdus] = useState('')
  /*
    Ce lista se editeaza: cadourile sau premiile.

    Un singur selector, comutat, in loc de doua blocuri identice unul sub altul. Actiunea are acum
    doua liste de produse — Cadoul Bonus, dat pentru Story, si premiul tombolei — iar ele nu au
    voie sa se amestece: salvarea uneia stinge ce nu mai e in ea, si fara comutator ar fi stins-o
    pe cealalta.
  */
  const [rolEditat, setRolEditat] = useState<'cadou' | 'premiu'>('cadou')
  const [selectie, setSelectie] = useState<Record<number, { cantitate: number; activ: boolean }>>(
    {},
  )
  const [initializatPt, setInitializatPt] = useState<string | null>(null)
  /*
    Ce e TASTAT in casuta de cantitate, cat timp e tastat.

    Limitele se puneau la fiecare apasare de tasta. Campul arata „1", omul punea cursorul la
    capat si scria 2, apoi 0 — adica „12", apoi „120" —, iar la a doua tasta valoarea sarea la
    50 si nu mai putea fi coborata: orice cifra in plus depasea iar maximul. Alexandru,
    22.09.2026: „incerc sa adaug cantitate 20, dar se pune automat 50".

    Deci textul ramane cum a fost scris pana la iesirea din casuta; abia atunci se aseaza in
    limite. Cat timp e valid, se scrie si in `selectie`, ca o salvare fara iesire din casuta sa
    duca valoarea corecta.
  */
  const [cantScrisa, setCantScrisa] = useState<Record<number, string>>({})

  // Se reinitializeaza si la comutarea rolului, nu doar la deschidere: altfel bifele cadoului ar
  // ramane pe ecran cand treci la premii.
  const cheieInit = detaliu.data ? `${detaliu.data.id}:${rolEditat}` : null
  if (detaliu.data && cheieInit !== initializatPt) {
    const s: Record<number, { cantitate: number; activ: boolean }> = {}
    for (const p of detaliu.data.produse.filter((x) => x.rol === rolEditat))
      s[p.productId] = { cantitate: p.cantitate, activ: p.activ }
    setSelectie(s)
    setInitializatPt(cheieInit)
  }

  const link = detaliu.data ? `${window.location.origin}/s/${detaliu.data.slug}` : ''

  return (
    // `lg`, nu `md`. Alexandru: „e prea îngustă, mă cam deranjează vizual". Avea dreptate și
    // dincolo de gust: pe 448 px, numele produselor din catalog se rup pe două rânduri, iar
    // rândurile câștigătorilor — nume, telefon, stare, proces-verbal — se împachetau în patru
    // etaje. Fereastra pornise ca un formular scurt și a ajuns ecranul principal al modulului.
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={detaliu.data?.denumire ?? 'Acțiune'}
      inchideLaClicInAfara={false}
    >
      {/*
        Configurarile, strânse sub un cap.

        Sunt sase-sapte campuri care se ating o data, la pornirea actiunii, si apoi aproape
        niciodata — dar stateau deschise deasupra a tot, iar linkul QR si produsele, adica lucrurile
        cautate zilnic, ajungeau sub doua ecrane de derulat.
      */}
      {detaliu.data && canWrite && !detaliu.data.inchisaLa && (
        <div className="mb-3">
          <Pliant titlu="Configurarea acțiunii" nota="denumire, texte, linkuri">
            <label className="mb-3 block">
              <span className="text-xs text-b4l-text-muted">Denumirea acțiunii</span>
              <input
                defaultValue={detaliu.data.denumire}
                onBlur={(e) => {
                  const nou = e.target.value.trim()
                  if (nou.length >= 3 && nou !== detaliu.data?.denumire) redenumeste.mutate(nou)
                  else e.target.value = detaliu.data?.denumire ?? ''
                }}
                className="mt-1 w-full rounded-lg border border-b4l-border px-3 py-2 text-sm"
              />
              <span className="mt-1 block text-xs text-b4l-text-muted">
                Apare pe afiș, pe formular și pe procesul-verbal. Se salvează când ieși din câmp.
              </span>
            </label>
            {/*
              Agentul acțiunii. Vezi mutația pentru de ce contează — gestiune, bon, vizibilitate.
            */}
            <label className="mb-3 block">
              <span className="text-xs text-b4l-text-muted">Agentul acțiunii</span>
              <select
                value={detaliu.data.agentUserId ?? ''}
                onChange={(e) =>
                  schimbaAgent.mutate(e.target.value ? Number(e.target.value) : null)
                }
                disabled={schimbaAgent.isPending}
                className="mt-1 w-full rounded-lg border border-b4l-border px-3 py-2 text-sm"
              >
                <option value="">— fără agent —</option>
                {(agenti.data ?? []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-xs text-b4l-text-muted">
                Cine lucrează acțiunea pe teren — nu cine a pornit-o. Din gestiunea lui ies
                mostrele, numele lui apare pe bonul de consum, și doar el o vede în lista de
                acțiuni.
              </span>
            </label>
            {/*
        Textul de deasupra formularului. Era scris in cod, deci identic la orice tombola — desi
        tocmai el spune ce primeste omul si ce nu, iar asta difera de la o actiune la alta.

        Gol = se intoarce cel implicit. Nu ramane un formular fara nicio explicatie.
      */}
            {detaliu.data && canWrite && !detaliu.data.inchisaLa && (
              <label className="mb-3 block">
                <span className="text-xs text-b4l-text-muted">Textul de deasupra formularului</span>
                <textarea
                  defaultValue={detaliu.data.textIntro ?? ''}
                  rows={2}
                  placeholder={
                    detaliu.data.tip === 'tombola'
                      ? 'Completează datele și intri la extragere. Nu primești produse la acest formular.'
                      : 'Gol = fără text introductiv.'
                  }
                  onBlur={(e) => {
                    const nou = e.target.value.trim()
                    if (nou !== (detaliu.data?.textIntro ?? '')) schimbaIntro.mutate(nou || null)
                  }}
                  className="mt-1 w-full rounded-lg border border-b4l-border px-3 py-2 text-sm"
                />
                <span className="mt-1 block text-xs text-b4l-text-muted">
                  Lasă gol ca să revii la textul implicit. Se salvează când ieși din câmp.
                </span>
              </label>
            )}
            {/*
        Afișul campaniei, în capul formularului public.

        Modelul cerut de Alexandru — campania Nestlé de la Mega Image — începe cu un banner, apoi
        formularul. Fără el, cine scanează codul QR ajunge direct pe câmpuri și nu are nicio
        confirmare vizuală că a nimerit unde trebuie.
      */}
            {detaliu.data && canWrite && !detaliu.data.inchisaLa && (
              <div className="mb-3">
                <span className="text-xs text-b4l-text-muted">Afișul campaniei</span>
                {detaliu.data.afisDataUri ? (
                  <div className="mt-1">
                    <img
                      src={detaliu.data.afisDataUri}
                      alt="Afișul campaniei"
                      className="w-full rounded-lg border border-b4l-border object-cover"
                    />
                    <button
                      onClick={() => stergeAfis.mutate()}
                      disabled={stergeAfis.isPending}
                      className="mt-1 text-xs text-b4l-red hover:underline"
                    >
                      Șterge afișul
                    </button>
                  </div>
                ) : (
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => {
                      const f = e.target.files?.[0]
                      if (f) incarcaAfis.mutate(f)
                      e.target.value = ''
                    }}
                    disabled={incarcaAfis.isPending}
                    className="mt-1 w-full rounded-lg border border-b4l-border px-3 py-2 text-sm"
                  />
                )}
                <span className="mt-1 block text-xs text-b4l-text-muted">
                  JPG, PNG sau WEBP, cel mult 2 MB. Raport 2:1 — lățimea dublă față de înălțime.
                  Pentru un ecran de telefon modern, cel puțin 1200 px lățime; sub atât se vede
                  neclar.
                </span>
              </div>
            )}

            {/*
        Regulamentul campaniei, scris pe ACTIUNE.

        Alexandru: „regulamentul — text editabil per actiune". Are sens: premiile, datele si
        locul difera de la o campanie la alta, iar OG 99/2000 art. 42 alin. (1) cere tocmai
        natura, numarul si valoarea comerciala a premiilor. Un text pe tip ar fi fost acelasi la
        toate tombolele, deci fals la fiecare.
      */}
            {/*
        Regulamentul: LINKUL intai, textul dupa el.

        Alexandru: „avem doua sectiuni cu regulamentul campaniei... se justifica?". Se justifica,
        dar nu asa cum aratau. Nu sunt doua locuri egale, sunt ALTERNATIVE: cand exista link, textul
        lipit nu se mai foloseste nicaieri. Cu acelasi nume si cu textul primul, cineva putea lipi
        regulamentul in caseta si crede ca l-a publicat, in timp ce formularul trimitea in alta
        parte.

        Deci linkul e primul, si e calea normala — textul autentificat notarial traieste pe site-ul
        firmei. Caseta ramane, dar ca rezerva, pentru campaniile fara pagina publica; cand linkul e
        pus, se inchide singura si o spune.
      */}
            {detaliu.data && canWrite && !detaliu.data.inchisaLa && (
              <label className="mb-3 block">
                <span className="text-xs text-b4l-text-muted">Regulamentul campaniei — link</span>
                <input
                  type="url"
                  defaultValue={detaliu.data.urlRegulament ?? ''}
                  placeholder="https://www.boost4life.ro/pages/regulament-scan-win"
                  onBlur={(e) => {
                    const nou = e.target.value.trim()
                    if (nou !== (detaliu.data?.urlRegulament ?? ''))
                      schimbaLinkRegulament.mutate(nou || null)
                  }}
                  className="mt-1 w-full rounded-lg border border-b4l-border px-3 py-2 text-sm"
                />
                <span className="mt-1 block text-xs text-b4l-text-muted">
                  Adresa regulamentului autentificat, de pe site. Acolo trimit bifa din formular și
                  butoanele din mesaje. Se salvează când ieși din câmp.
                </span>
              </label>
            )}

            {detaliu.data && canWrite && !detaliu.data.inchisaLa && (
              <label className="mb-3 block">
                <span className="text-xs text-b4l-text-muted">
                  Regulamentul campaniei — text în CRM
                  {detaliu.data.urlRegulament && ' (nefolosit, ai pus link)'}
                </span>
                <textarea
                  defaultValue={detaliu.data.textRegulament ?? ''}
                  rows={detaliu.data.urlRegulament ? 2 : 6}
                  disabled={Boolean(detaliu.data.urlRegulament)}
                  placeholder="Lipește aici textul regulamentului autentificat notarial. Gol = formularul nu cere acceptarea lui."
                  onBlur={(e) => {
                    const nou = e.target.value.trim()
                    if (nou !== (detaliu.data?.textRegulament ?? ''))
                      schimbaRegulament.mutate(nou || null)
                  }}
                  className="mt-1 w-full rounded-lg border border-b4l-border px-3 py-2 font-mono text-xs disabled:bg-b4l-bg disabled:text-b4l-text-dim"
                />
                {detaliu.data.urlRegulament ? (
                  <span className="mt-1 block text-xs text-b4l-text-muted">
                    Câtă vreme există link, textul de aici nu se folosește nicăieri. Golește linkul
                    de mai sus dacă vrei să afișezi regulamentul din CRM.
                  </span>
                ) : (
                  <>
                    <span className="mt-1 block text-xs text-b4l-amber-dark">
                      Se <strong>lipește</strong> textul autentificat notarial, nu se redactează
                      aici. La o loterie publicitară regulamentul trebuie autentificat înainte de
                      startul campaniei (OG 99/2000 art. 43 alin. 1), iar forma din CRM trebuie să
                      fie identică cu cea de la notar. Dacă diferă, cea de aici nu apără pe nimeni.
                    </span>
                    <span className="mt-1 block text-xs text-b4l-text-muted">
                      Cât timp e completat, formularul cere o bifă separată de acceptare și o
                      păstrează ca dovadă. Se salvează când ieși din câmp.
                    </span>
                  </>
                )}
              </label>
            )}

            {/*
        Politica de confidentialitate, ca link, pe ACTIUNE.

        Alexandru: „linkul catre conditii GDPR de pe pagina de inscriere trebuie sa fie acesta
        (...) hai sa-l facem configurabil aici".

        Ramane si in Setari, ca implicit al firmei — adresa e de obicei aceeasi peste tot. Dar art.
        3.5 din regulament indica o adresa anume pentru campania asta, iar regulamentul e per
        actiune; linkul trebuie sa-l poata urma.
      */}
            {detaliu.data && canWrite && !detaliu.data.inchisaLa && (
              <label className="mb-3 block">
                <span className="text-xs text-b4l-text-muted">
                  Politica de confidențialitate (link)
                </span>
                <input
                  type="url"
                  defaultValue={detaliu.data.urlPolitica ?? ''}
                  placeholder="https://www.boost4life.ro/pages/politica-de-confidentialitate"
                  onBlur={(e) => {
                    const nou = e.target.value.trim()
                    if (nou !== (detaliu.data?.urlPolitica ?? ''))
                      schimbaPolitica.mutate(nou || null)
                  }}
                  className="mt-1 w-full rounded-lg border border-b4l-border px-3 py-2 text-sm"
                />
                <span className="mt-1 block text-xs text-b4l-text-muted">
                  Apare ca link în formular, sub bifa de informare. Gol = se folosește linkul din
                  Sampling → Setări. Se salvează când ieși din câmp.
                </span>
              </label>
            )}
            {/*
        Conditia in care se primeste cadoul, la tombola. Apare langa bifa din formular.

        Victor: „chiar ii aducem la cunostinta ca trebuie sa faca acel lucru ca sa poata bifa".
        Conditia difera de la o actiune la alta — la Sense4Fit e un Story cu tag — deci se scrie
        aici, nu in cod.
      */}
            {detaliu.data?.tip === 'tombola' && canWrite && !detaliu.data.inchisaLa && (
              <label className="mb-3 block">
                <span className="text-xs text-b4l-text-muted">
                  Condiția pentru cadou (apare pe formular, la pașii 2 și 3)
                </span>
                <textarea
                  defaultValue={detaliu.data.textCadou ?? ''}
                  rows={2}
                  placeholder="Ex.: Postează un Story de la eveniment cu tag @boost4life.ro vizibil și arată-l echipei Boost4Life."
                  onBlur={(e) => {
                    const nou = e.target.value.trim()
                    if (nou !== (detaliu.data?.textCadou ?? '')) schimbaCadou.mutate(nou || null)
                  }}
                  className="mt-1 w-full rounded-lg border border-b4l-border px-3 py-2 text-sm"
                />
                <span className="mt-1 block text-xs text-b4l-text-muted">
                  Apare doar dacă acțiunea are cel puțin un produs ales mai jos. Îl vede
                  participantul de două ori: la pasul 2, <strong>înainte</strong> să aleagă dacă
                  vrea produsul, și pe ecranul de confirmare, ca reamintire. Scrie aici tot ce
                  trebuie să conțină postarea — e singurul loc din care află. Se salvează când ieși
                  din câmp.
                </span>
              </label>
            )}
          </Pliant>
        </div>
      )}

      {detaliu.isLoading || catalog.isLoading ? (
        <p className="text-sm text-b4l-text-muted">Se încarcă…</p>
      ) : !detaliu.data ? (
        <p className="text-sm text-b4l-red">Acțiunea nu a putut fi încărcată.</p>
      ) : (
        <div className="space-y-4">
          {/*
            La tombolă nu se dau mostre, deci codul QR merge fără niciun produs — serverul
            scutește explicit tombola de verificarea asta. Avertismentul apărea și acolo, cerând
            să bifezi un produs unei acțiuni care prin definiție n-are.
          */}
          {detaliu.data.tip !== 'tombola' &&
            !detaliu.data.faraMostre &&
            detaliu.data.produse.filter((p) => p.activ).length === 0 && (
              <div className="rounded-lg bg-b4l-amber-light p-3 text-sm text-b4l-amber-dark">
                <strong>Codul QR nu funcționează încă.</strong> Bifează mai jos cel puțin un produs
                și apasă <em>Salvează produsele</em>. Până atunci, cine scanează primește „Acțiune
                indisponibilă”.
              </div>
            )}
          <div className="rounded-lg bg-b4l-blue-light p-3">
            <div className="flex items-center gap-2 text-sm font-medium text-b4l-blue-dark">
              <QrCode size={16} /> Linkul pentru codul QR
            </div>
            <div className="mt-2 flex items-center gap-2">
              <code className="flex-1 truncate rounded bg-white px-2 py-1 text-xs">{link}</code>
              <button
                onClick={() => {
                  void navigator.clipboard.writeText(link)
                  setCopiat(true)
                  setTimeout(() => setCopiat(false), 1500)
                }}
                className="rounded p-1 text-b4l-blue-dark hover:bg-white"
                title="Copiază"
              >
                {copiat ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>
            <p className="mt-2 text-xs text-b4l-blue-dark">
              Adaugă <code>?a=ID_AGENT</code> la link ca predările să fie atribuite unui agent.
            </p>
            {qr.data && (
              <div className="mt-3 flex items-center gap-3">
                <img src={qr.data.png} alt="Cod QR" className="h-28 w-28 rounded bg-white p-1" />
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => {
                      void downloadFile(
                        `/marketing/sampling/actiuni/${id}/afis`,
                        {},
                        `afis-${detaliu.data?.slug ?? id}.pdf`,
                      ).catch((e: Error) => toast.error(e.message))
                    }}
                    className="inline-flex items-center gap-1 text-sm text-b4l-blue-dark hover:underline"
                  >
                    <Printer size={14} /> Afiș de stand (PDF)
                  </button>
                  <button
                    onClick={() => {
                      void downloadFile(
                        `/marketing/sampling/actiuni/${id}/qr.svg`,
                        {},
                        `qr-${detaliu.data?.slug ?? id}.svg`,
                      ).catch((e: Error) => toast.error(e.message))
                    }}
                    className="inline-flex items-center gap-1 text-sm text-b4l-blue-dark hover:underline"
                    title="Vectorial — se scalează la orice dimensiune, pentru tipar"
                  >
                    <Download size={14} /> Doar codul (SVG, pentru tipar)
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="text-sm text-b4l-text-muted">
            {[
              TIP_LABEL[detaliu.data.tip] ?? detaliu.data.tip,
              detaliu.data.agentName,
              detaliu.data.branchLabel ?? detaliu.data.locatie,
              detaliu.data.tip !== 'tombola'
                ? (MOD_BON_LABEL[detaliu.data.modBon] ?? detaliu.data.modBon)
                : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </div>

          {/*
            UNDE stă acțiunea. Lipsea, și asta a costat o căutare degeaba: Adrian a făcut
            „Farmacia Tei Craiova" și i-a spus „eveniment", iar Alexandru n-a găsit-o în lista
            de evenimente fiindcă stă într-un program trimestrial (28.09.2026). Panoul știa
            unde e, dar nu spunea.

            E link: de aici se ajunge la costuri, la necesar și la restul filelor containerului.
          */}
          {detaliu.data.container && (
            <div className="text-sm">
              <span className="text-b4l-text-muted">Face parte din: </span>
              <Link
                to={`/marketing/evenimente/${detaliu.data.container.id}`}
                className="text-b4l-blue-dark hover:underline"
              >
                <span className="font-mono text-xs">{detaliu.data.container.cod}</span>{' '}
                {detaliu.data.container.titlu}
              </Link>
            </div>
          )}

          {detaliu.data.tip === 'tombola' && (
            <TombolaExtrageri actiuneId={id} canWrite={canWrite} />
          )}

          {/*
            Produsele se aleg si la tombola. Erau ascunse acolo, fiindca tombola insemna „doar
            inscrieri" — dar acum o tombola poate da si un cadou fiecarui inscris, si atunci
            predarea merge ca la farmacie: semnatura, proces-verbal, scadere din gestiune.

            Fara produse bifate, tombola ramane exact cum era. Regula o decid datele: produse
            configurate = se preda.
          */}
          {/*
            Comutatorul stă AFARĂ din secțiunea pliată, și deasupra ei: se apasă în mijlocul
            evenimentului, când s-au terminat produsele, iar atunci nimeni nu caută prin secțiuni
            închise. Lista de produse rămâne neatinsă dedesubt.
          */}
          {canWrite && !detaliu.data.inchisaLa && detaliu.data.tip === 'tombola' && (
            <label className="flex items-start gap-2 rounded-lg border border-b4l-border p-3">
              <input
                type="checkbox"
                checked={detaliu.data.cadouOprit}
                onChange={(e) => schimbaCadouOprit.mutate(e.target.checked)}
                className="mt-0.5 h-5 w-5"
              />
              <span className="text-sm text-b4l-text">
                Nu mai oferi cadoul pe formular
                <span className="mt-1 block text-xs text-b4l-text-muted">
                  Pentru când s-au terminat produsele. Butonul de cerere a produsului dispare de pe
                  formular, iar înscrierea la tombolă merge mai departe. Lista de produse rămâne
                  configurată, iar la stand se poate preda în continuare, dacă mai apare marfă.
                </span>
              </span>
            </label>
          )}

          {/*
            Bifa sta LANGA produse, nu in „Configurarea actiunii": intrebarea „se preda ceva?" se
            pune exact cand te uiti la ce se preda, iar cele doua se contrazic usor — o actiune cu
            produse bifate si cu „fara mostre" pus arata un formular care cere semnatura degeaba.
          */}
          {detaliu.data.tip !== 'tombola' && (
            <label className="mb-3 flex cursor-pointer items-start gap-2 rounded-lg border border-b4l-border bg-b4l-bg p-3">
              <input
                type="checkbox"
                checked={detaliu.data.faraMostre}
                disabled={!canWrite || schimbaFaraMostre.isPending}
                onChange={(e) => schimbaFaraMostre.mutate(e.target.checked)}
                className="mt-0.5 h-4 w-4"
              />
              <span className="text-sm">
                <span className="block font-medium text-b4l-text">
                  Doar colectare de date, fără mostre
                </span>
                {/*
                  Textul spune acum adevarul. Pana pe 24.09.2026 zicea „produsele de mai jos raman
                  configurate, dar nu se predau" — fals: predarea de la stand mergea in continuare,
                  si chiar asa se lucra in farmacia Tei. Doua propozitii care se bateau cap in cap
                  pe acelasi ecran.

                  De cand predarea se face DOAR de la stand, bifa inseamna exact ce scrie: aici nu
                  se da nimic. Iar serverul o tine: nu se poate bifa peste o actiune cu produse.
                */}
                <span className="block text-xs text-b4l-text-muted">
                  Bifat, acțiunea nu dă mostre deloc — strânge doar datele de contact. Nu se pot
                  adăuga produse cât timp bifa e pusă. Debifeaz-o dacă se predau mostre: produsele
                  se aleg mai jos, iar predarea o face agentul, din{' '}
                  <strong>Predare la stand</strong>.
                </span>
              </span>
            </label>
          )}

          {/*
            Scenariul alternativ, tot langa produse: „se preda ceva?" si „cine preda?" sunt aceeasi
            intrebare pusa de doua ori, si se citesc impreuna.

            Nu apare la tombola — premiul se cuvine cui a fost extras — si nici cand actiunea e
            marcata fara mostre. Serverul refuza oricum amandoua; ascunderea e ca sa nu se ofere o
            bifa care nu se poate pune.
          */}
          {detaliu.data.tip !== 'tombola' && !detaliu.data.faraMostre && (
            <label className="mb-3 flex cursor-pointer items-start gap-2 rounded-lg border border-b4l-border bg-b4l-bg p-3">
              <input
                type="checkbox"
                checked={detaliu.data.predareLaSemnatura}
                disabled={!canWrite || schimbaPredareLaSemnatura.isPending}
                onChange={(e) => schimbaPredareLaSemnatura.mutate(e.target.checked)}
                className="mt-0.5 h-4 w-4"
              />
              <span className="text-sm">
                <span className="block font-medium text-b4l-text">
                  Predarea se face la semnătură, în formular
                </span>
                <span className="block text-xs text-b4l-text-muted">
                  Pentru evenimentele mici, unde datele se strâng și produsele se dau în același
                  timp. Participantul semnează <strong>o singură dată</strong>, și pentru acord, și
                  de primire, iar procesul-verbal și bonul de consum se fac pe loc — nu mai e nevoie
                  de <strong>Predare la stand</strong>. Kitul trebuie ales mai jos: la semnătură nu
                  mai alege nimeni ce primește participantul.
                </span>
              </span>
            </label>
          )}

          {/*
            Perechea contradictorie — bifa pusa PESTE produse — nu se mai poate crea, dar exista in
            baza de pe vremea cand se putea. Se semnaleaza, cu ce e de facut.
          */}
          {detaliu.data.faraMostre && detaliu.data.produse.filter((p) => p.activ).length > 0 && (
            <div className="mb-3 rounded-lg bg-b4l-amber-light p-3 text-sm text-b4l-amber-dark">
              Acțiunea are produse configurate, dar e marcată <strong>fără mostre</strong>. Cele
              două nu mai merg împreună: debifează mai sus dacă produsele chiar se predau, sau
              scoate-le dacă aici doar se colectează date.
            </div>
          )}

          {/*
            Cât a costat acțiunea, pe scurt.

            Cine deschide panoul vede cifra pe loc, fără să treacă prin fila Costuri și să
            filtreze. Apare doar când chiar există ceva: un „0,00 lei" pe o acțiune de ieri n-ar
            fi o informație, ci un rând în plus.
          */}
          <CostActiune id={id} />

          <Pliant
            titlu="Produse și premii"
            nota={(() => {
              const n = detaliu.data.produse.filter((p) => p.activ).length
              return n === 0 ? 'niciun produs' : n === 1 ? '1 produs' : `${n} produse`
            })()}
          >
            <div>
              {/*
                Comutatorul intre cele doua liste, doar la tombola.

                Campania SCAN & WIN are DOUA mecanisme independente — asa scrie in preambulul
                regulamentului si la art. 9.1 — iar pana pe 27.08 aplicatia stia doar unul. Cine
                apasa butonul de predare pe un castigator i-ar fi dat o creatina in loc de kit,
                fiindca aia era singura marfa pe care o cunostea.
              */}
              {detaliu.data.tip === 'tombola' && (
                <div className="mt-2 inline-flex rounded-lg bg-b4l-bg p-0.5">
                  {(['cadou', 'premiu'] as const).map((r) => (
                    <button
                      key={r}
                      onClick={() => setRolEditat(r)}
                      className={`rounded-md px-3 py-1.5 text-sm ${
                        rolEditat === r
                          ? 'bg-white font-medium text-b4l-text shadow-sm'
                          : 'text-b4l-text-muted'
                      }`}
                    >
                      {r === 'cadou' ? 'Cadou pentru Story' : 'Premiul tombolei'}
                    </button>
                  ))}
                </div>
              )}
              {/*
                Textul de dinainte spunea „sunt produsele bifate ca mostre in Setari" — nu mai e
                adevarat. Lista contine acum toate suplimentele alimentare, plus orice s-a mai
                dat vreodata la sampling. Bifa se pune singura la folosire.
              */}
              {/*
                Textul de dinainte vorbea despre Story si despre „pasii 2 si 3 ai formularului" pe
                ORICE actiune — desi la o vizita in farmacie nu exista niciun Story, iar formularul
                nu mai are pasi de predare. Acum spune ce inseamna la actiunea deschisa.

                Randul despre cantitate e cel mai important: de la 24.09.2026 cifra de langa fiecare
                produs inseamna cat s-a alocat pe TOATA actiunea, nu cat primeste fiecare om. Fara
                propozitia asta, cine pune „100" crede ca da 100 de bucati unei singure persoane.
              */}
              <p className="mt-2 text-xs text-b4l-text-muted">
                {rolEditat === 'premiu'
                  ? 'Ce primește câștigătorul extras la tombolă. Se predă la stand, din tabul „Premii tombolă”. Aici cantitatea e compoziția kitului: atât primește FIECARE câștigător.'
                  : detaliu.data.tip === 'tombola'
                    ? 'Ce primește cine postează Story-ul. Se predă la stand, după ce consultantul verifică.'
                    : 'Mostrele care se pot da la această acțiune. Cantitatea e cât s-a alocat pe TOATĂ acțiunea, nu cât primește fiecare participant: agentul bifează și scrie cantitatea la predare, în Predare la stand.'}{' '}
                Suplimentele alimentare din catalog, plus orice s-a mai dat vreodată la sampling.
              </p>
              {/*
                Incarcarea dintr-un kit. Alexandru: „premiul tombolei e un kit, pe care l-am
                definit la kit-uri".

                Aparea doar la premii, si a fost o restrangere gresita: Alexandru a cautat butonul
                pe tabul de cadouri si n-a gasit nimic. Un kit se poate incarca la fel de bine si
                acolo — la o farmacie se da tot un set de produse. Butonul apare pe ambele tabur,
                si scrie in eticheta unde ajunge.

                Kiturile exista tocmai ca sa nu alegi aceleasi produse de doua ori. Fara butonul
                asta, ecranul de premii cerea sa reconstruiesti de mana ce era deja definit — si
                cele doua liste ar fi divergat la prima corectura facuta doar intr-un loc.

                ADAUGA, nu inlocuieste: ce e deja bifat ramane. Un kit incarcat peste altul le
                aduna, iar ce nu vrei se debifeaza. Mai bine asa decat sa stearga in tacere munca
                de dinainte.
              */}
              {(kituri.data?.filter((k) => k.activ).length ?? 0) > 0 && (
                <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg bg-b4l-bg p-2">
                  <span className="text-xs text-b4l-text-muted">
                    Încarcă {rolEditat === 'premiu' ? 'premiul' : 'cadoul'} dintr-un kit:
                  </span>
                  {kituri.data
                    ?.filter((k) => k.activ)
                    .map((k) => (
                      <button
                        key={k.id}
                        onClick={() => {
                          setSelectie((prev) => {
                            const nou = { ...prev }
                            for (const l of k.linii) {
                              nou[l.productId] = { cantitate: l.cantitate, activ: true }
                            }
                            return nou
                          })
                          toast.success(
                            `${k.denumire}: ${k.linii.length} ${
                              k.linii.length === 1 ? 'produs adăugat' : 'produse adăugate'
                            }. Nu uita să salvezi.`,
                          )
                        }}
                        className="rounded-lg border border-b4l-border bg-white px-3 py-1.5 text-sm text-b4l-text"
                        title={k.linii.map((l) => `${l.denumire} ×${l.cantitate}`).join(', ')}
                      >
                        {k.denumire}{' '}
                        <span className="text-xs text-b4l-text-muted">({k.linii.length})</span>
                      </button>
                    ))}
                </div>
              )}

              {/*
                Cautare. Lista era de vreo zece produse cat timp cerea bifa manuala; acum tine tot
                raftul de suplimente, si o insiruire de bife nu se mai poate parcurge cu ochiul.
                Ce e deja bifat ramane vizibil oricare ar fi cautarea — altfel dispare de sub mana
                omului taman cand verifica ce a ales.
              */}
              {(catalog.data?.produse.length ?? 0) > 8 && (
                <input
                  value={cautaProdus}
                  onChange={(e) => setCautaProdus(e.target.value)}
                  placeholder="Caută după denumire sau cod"
                  className="mt-2 w-full rounded-lg border border-b4l-border px-3 py-1.5 text-sm"
                />
              )}
              {!catalog.data?.produse.length ? (
                <p className="mt-2 text-sm text-b4l-amber-dark">
                  Catalogul nu are niciun supliment alimentar. Adaugă-le din Setări → Catalog
                  produse, „Adaugă articole din ExpertAccounts”.
                </p>
              ) : (
                <div className="mt-2 space-y-2">
                  {catalog.data.produse
                    .filter((p) => {
                      const t = cautaProdus.trim().toLowerCase()
                      if (!t) return true
                      // Ce e bifat nu se ascunde niciodata.
                      if (selectie[p.id]?.activ) return true
                      return p.name.toLowerCase().includes(t) || p.sku.toLowerCase().includes(t)
                    })
                    .map((p) => {
                      const sel = selectie[p.id]
                      return (
                        <div key={p.id} className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={Boolean(sel?.activ)}
                            disabled={!canWrite || Boolean(detaliu.data?.inchisaLa)}
                            onChange={(e) =>
                              setSelectie((s) => ({
                                ...s,
                                [p.id]: {
                                  cantitate: s[p.id]?.cantitate ?? 1,
                                  activ: e.target.checked,
                                },
                              }))
                            }
                            className="h-4 w-4"
                          />
                          <span className="flex-1">
                            {p.name}
                            <span className="ml-1 text-xs text-b4l-text-dim">{p.sku}</span>
                          </span>
                          <input
                            type="number"
                            min={1}
                            max={50}
                            value={cantScrisa[p.id] ?? String(sel?.cantitate ?? 1)}
                            disabled={!canWrite || !sel?.activ}
                            onChange={(e) => {
                              const scris = e.target.value
                              setCantScrisa((t) => ({ ...t, [p.id]: scris }))
                              /*
                                Intreg, intre 1 si 50 — exact ce accepta API-ul. Cat timp ce s-a
                                scris e deja valid, se comite; altfel se asteapta iesirea din
                                casuta, ca sa nu i se schimbe cifrele sub degete.
                              */
                              const n = Number(scris)
                              if (Number.isInteger(n) && n >= 1 && n <= 50) {
                                setSelectie((s) => ({
                                  ...s,
                                  [p.id]: { cantitate: n, activ: s[p.id]?.activ ?? true },
                                }))
                              }
                            }}
                            onBlur={() => {
                              const n = Math.min(
                                50,
                                Math.max(1, Math.round(Number(cantScrisa[p.id]) || 1)),
                              )
                              setSelectie((s) => ({
                                ...s,
                                [p.id]: { cantitate: n, activ: s[p.id]?.activ ?? true },
                              }))
                              setCantScrisa((t) => {
                                const rest = { ...t }
                                delete rest[p.id]
                                return rest
                              })
                            }}
                            title="Câte bucăți din produsul ăsta primește o persoană. Între 1 și 50."
                            className="w-16 rounded border border-b4l-border px-2 py-1 text-sm disabled:opacity-40"
                          />
                        </div>
                      )
                    })}
                </div>
              )}
            </div>
          </Pliant>

          {/*
            Pozele stau ULTIMELE si deschise, nu intr-un pliant.

            Sunt singurul lucru din fereastra pe care il pune cineva de pe telefon, in picioare,
            langa stand. Un pliant in plus de apasat, cu degetul, pe un ecran mic, e exact
            genul de pas care face ca pozele sa nu mai fie incarcate deloc.
          */}
          <div className="rounded-lg border border-b4l-border p-3">
            <DocumenteContainer tip="actiune" parinteId={id} canWrite={canWrite} />
          </div>
        </div>
      )}

      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          Închide
        </Button>
        {canWrite && detaliu.data && !detaliu.data.inchisaLa && (
          <Button
            disabled={salveazaProduse.isPending}
            onClick={() =>
              salveazaProduse.mutate({
                rol: rolEditat,
                linii: Object.entries(selectie).map(([pid, v]) => ({
                  productId: Number(pid),
                  cantitate: v.cantitate,
                  activ: v.activ,
                })),
              })
            }
          >
            {salveazaProduse.isPending
              ? 'Se salvează…'
              : rolEditat === 'premiu'
                ? 'Salvează premiile'
                : 'Salvează cadourile'}
          </Button>
        )}
      </div>
    </Modal>
  )
}

/** „august 2026" — pentru denumirea propusă automat la o vizită în farmacie. */

/**
 * Când are rost „Pornește din nou".
 *
 * Prima variantă îl arăta pe acțiunile ACTIVE, adică fix acolo unde n-are sens: o acțiune care
 * merge acum n-are de ce să fie repornită, iar apăsarea producea un geamăn cu același nume, în
 * aceeași zi. Locul lui e pe cele terminate.
 *
 * „Terminată" înseamnă închisă — sau rămasă deschisă dintr-o zi trecută, cazul obișnuit pe teren,
 * unde nimeni nu închide vizita de luna trecută.
 *
 * Fără produse active n-are ce copia: acțiunea nouă ar porni cu un cod QR mort. Tombola face
 * excepție, fiindcă nu dă nimic.
 */
export function sePoateRelua(
  a: Pick<Actiune, 'tip' | 'nrProduseActive' | 'inchisaLa' | 'dataStart'>,
): boolean {
  const areCeCopia = a.tip === 'tombola' || a.nrProduseActive > 0
  if (!areCeCopia) return false
  if (a.inchisaLa) return true
  if (!a.dataStart) return false
  const azi = new Date()
  azi.setHours(0, 0, 0, 0)
  return new Date(a.dataStart) < azi
}

function ButonContext({
  icon,
  titlu,
  detaliu,
  onClick,
}: {
  icon: React.ReactNode
  titlu: string
  detaliu: string
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-3 rounded-lg border border-b4l-border bg-white p-3 text-left transition hover:border-b4l-blue"
    >
      <span className="text-b4l-text-muted">{icon}</span>
      <span>
        <span className="block text-sm font-medium text-b4l-text">{titlu}</span>
        <span className="block text-xs text-b4l-text-muted">{detaliu}</span>
      </span>
    </button>
  )
}

function perioada(start: string | null, final: string | null): string {
  if (!start && !final) return '—'
  // Actiunile noi au doar data de inceput. Un „19.08.2026 – …" ar fi aratat ca o scapare.
  if (start && !final) return zi(start)
  return `${start ? zi(start) : '…'} – ${final ? zi(final) : '…'}`
}

/** Ziua de azi, in formatul cerut de input-ul de tip date. */

/** Data cu ora si secunda. Cerut pe datele colectate: la tombola, ordinea inscrierilor conteaza. */
function ziOra(iso: string): string {
  const d = new Date(iso)
  return `${d.toLocaleDateString('ro-RO')} ${d.toLocaleTimeString('ro-RO', { hour12: false })}`
}

function zi(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`
}

function mesaj(e: unknown): string {
  const err = e as { response?: { data?: { error?: { message?: string } } } }
  return err.response?.data?.error?.message ?? 'A apărut o eroare'
}

/** Cum se numesc textele pe ecran. Cheia e chiar tipul actiunii — asa le regaseste serverul. */
const ETICHETA_TEXT: Record<string, string> = {
  mostre: 'Informare — predare de mostre',
  farmacie: 'Informare — acțiuni în farmacie',
  eveniment: 'Informare — evenimente',
  tombola: 'Informare — tombolă',
  marketing: 'Comunicări comerciale',
  informare: 'Informare generală (rezervă)',
}

/** Numele scurt, pentru tab. Cel lung ramane in cardul deschis. */
const ETICHETA_SCURTA: Record<string, string> = {
  mostre: 'Mostre',
  farmacie: 'Farmacie',
  eveniment: 'Evenimente',
  tombola: 'Tombolă',
  promo: 'Promoțional',
  marketing: 'Comunicări comerciale',
  informare: 'Rezervă',
}

/** Tipurile care au acum o rezervă bună — textul de mostre — deci lipsa lor nu e o problemă. */
const PE_TIP = new Set(['farmacie', 'eveniment', 'promo'])

const DESCRIERE_TEXT: Record<string, string> = {
  mostre:
    'Apare la TOATE acțiunile care predau produse — farmacie, eveniment, promoțional — indiferent de tip. Scrie-l o singură dată aici, în loc să-l copiezi în trei locuri de unde s-ar despărți la prima corectură. Un text de tip anume, dacă există, bate peste el. Trebuie să fie COMPLET: pe formular nu mai apare nimic lângă el.',
  farmacie:
    'Doar dacă vrei o formulare anume la farmacii, diferită de cea de la „Mostre". Lasă-l gol și se folosește textul de mostre. Trebuie să fie COMPLET: pe formular nu mai apare nimic lângă el, deci scrie și cine e operatorul, ce drepturi are participantul și cât se păstrează datele.',
  eveniment:
    'Doar dacă vrei o formulare anume la evenimente. Lasă-l gol și se folosește textul de mostre. Trebuie să fie COMPLET: pe formular nu mai apare nimic lângă el.',
  tombola:
    'Apare la tombole, care au alt temei decât predarea de mostre. Trebuie să fie COMPLET: pe formular nu mai apare nimic lângă el.',
  promo:
    'Doar dacă vrei o formulare anume la predările promoționale — influenceri și alte predări directe. Lasă-l gol și se folosește textul de mostre.',
  marketing: 'Opțională. Apare lângă bifa de marketing, care rămâne nebifată implicit.',
  informare:
    'Rezerva de pe urmă. Se folosește doar când nu există nici text de tip, nici text de mostre — de pildă la o tombolă fără text propriu. Fără ea, procesul-verbal se emite cu avertisment.',
}

interface Text {
  id: number
  tip: string
  versiune: string
  continut: string
  activ: boolean
  createdAt: string
}

/**
 * Textele juridice. Nu se editează niciodată una existentă — se adaugă versiune nouă.
 *
 * Motivul stă în dovadă: fiecare semnătură reține id-ul textului afișat atunci. Dacă textul s-ar
 * putea rescrie, procesele-verbale deja emise ar începe să pretindă că omul a citit altceva.
 */
function TabTexte({ canTexte }: { canTexte: boolean }) {
  const queryClient = useQueryClient()
  type TipText =
    | 'informare'
    | 'mostre'
    | 'farmacie'
    | 'eveniment'
    | 'tombola'
    | 'promo'
    | 'marketing'
  const [adaugaTip, setAdaugaTip] = useState<TipText | null>(null)

  const texte = useQuery({
    queryKey: ['sampling', 'texte'],
    queryFn: async () => {
      const r = await api.get<Text[]>('/marketing/sampling/texte')
      return r.data
    },
  })

  const dezactiveaza = useMutation({
    mutationFn: async (id: number) => api.post(`/marketing/sampling/texte/${id}/dezactiveaza`),
    onSuccess: () => {
      toast.success('Versiunea nu mai e activă')
      void queryClient.invalidateQueries({ queryKey: ['sampling', 'texte'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  const activeaza = useMutation({
    mutationFn: async (id: number) => api.post(`/marketing/sampling/texte/${id}/activeaza`),
    onSuccess: () => {
      toast.success('Versiune activată')
      void queryClient.invalidateQueries({ queryKey: ['sampling', 'texte'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  const adauga = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      api.post('/marketing/sampling/texte', body),
    onSuccess: () => {
      toast.success('Versiune adăugată')
      setAdaugaTip(null)
      void queryClient.invalidateQueries({ queryKey: ['sampling', 'texte'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  const peTip = (tip: string) => (texte.data ?? []).filter((t) => t.tip === tip)

  /*
    Un text pe ecran, nu toate cinci una sub alta.

    Solicitare: „sunt greu de urmarit asa, vreau sa fie tab-uri separate". Cinci carduri
    stivuite, fiecare cu versiunile lui, faceau o pagina pe care o derulai cautand unde se
    termina unul si incepe altul. Tab-urile spun si cate ceva ce stiva nu spunea: care tipuri
    n-au inca text.
  */
  const [tab, setTab] = useState<TipText>('mostre')
  /*
    „Mostre" e primul si e cel deschis implicit: acolo se scrie textul care se aplica la toate
    actiunile care predau produse. Cele pe tip raman pentru cand chiar vrei alta formulare
    intr-un singur loc (Alexandru, 22.09.2026).
  */
  const TIPURI = [
    'mostre',
    'farmacie',
    'eveniment',
    'tombola',
    'promo',
    'marketing',
    'informare',
  ] as const

  return (
    <div className="space-y-4">
      <div className="-mx-1 flex flex-wrap gap-1 border-b border-b4l-border">
        {TIPURI.map((t) => {
          const areActiv = (texte.data ?? []).some((x) => x.tip === t && x.activ)
          return (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm ${
                tab === t
                  ? 'border-b4l-green font-medium text-b4l-green-dark'
                  : 'border-transparent text-b4l-text-muted hover:text-b4l-text'
              }`}
            >
              {ETICHETA_SCURTA[t]}
              {/* Bulina rosie = tipul asta n-are text activ si merge pe rezerva. */}
              {/* Bulina apare doar unde lipsa chiar doare: mostre, tombola si marketing. */}
              {!areActiv && t !== 'informare' && !PE_TIP.has(t) && (
                <span className="h-1.5 w-1.5 rounded-full bg-b4l-red" title="Fără text activ" />
              )}
            </button>
          )
        })}
      </div>

      {[tab].map((tip) => {
        const lista = peTip(tip)
        const activ = lista.find((t) => t.activ)
        return (
          <Card key={tip}>
            <div className="flex items-start justify-between gap-2 p-4">
              <div>
                <h3 className="font-medium text-b4l-text">{ETICHETA_TEXT[tip]}</h3>
                <p className="mt-1 text-sm text-b4l-text-muted">{DESCRIERE_TEXT[tip]}</p>
              </div>
              {canTexte && (
                <Button variant="secondary" onClick={() => setAdaugaTip(tip)}>
                  Versiune nouă
                </Button>
              )}
            </div>

            {!activ && tip !== 'marketing' && tip !== 'informare' && (
              <p className="mx-4 mb-4 rounded-lg bg-b4l-amber-light p-3 text-sm text-b4l-amber-dark">
                {PE_TIP.has(tip)
                  ? 'Nicio versiune activă — și e în regulă. Acțiunile de tipul ăsta folosesc textul de la „Mostre". Scrie aici doar dacă vrei o formulare diferită.'
                  : 'Nicio versiune activă. Se folosește informarea de rezervă. Dacă nici aceea nu există, formularul le spune participanților, în roșu, că textul lipsește — dar nu îi împiedică să semneze.'}
              </p>
            )}

            {lista.length > 0 && (
              <div className="border-t border-b4l-border">
                {lista.map((t) => (
                  <div key={t.id} className="flex items-start gap-3 border-b border-b4l-border p-3">
                    <div className="w-20 shrink-0">
                      {/*
                        Versiunea se scrie cum a scris-o omul. Lipeam „v" in fata, si cine punea
                        „V2" citea pe ecran „vV2".
                      */}
                      <div className="font-mono text-sm">
                        {/^v/i.test(t.versiune) ? t.versiune : `v${t.versiune}`}
                      </div>
                      {t.activ ? (
                        <>
                          <span className="rounded-full bg-b4l-green-light px-2 py-0.5 text-xs text-b4l-green-dark">
                            Activă
                          </span>
                          {/*
                            Se putea doar ACTIVA alta versiune, deci un text de tip nu mai iesea
                            niciodata din circulatie. Conteaza de cand exista textul de „Mostre":
                            cat timp cel de tip ramane activ, el bate peste el.
                          */}
                          {canTexte && PE_TIP.has(tip) && (
                            <button
                              onClick={() => {
                                if (
                                  window.confirm(
                                    'Dezactivezi versiunea asta? Acțiunile de tipul ăsta vor ' +
                                      'folosi textul de la „Mostre". Versiunea rămâne în listă și ' +
                                      'se poate reactiva oricând.',
                                  )
                                )
                                  dezactiveaza.mutate(t.id)
                              }}
                              className="block text-xs text-b4l-text-muted hover:underline"
                            >
                              {/* Perechea lui „Activează", de pe versiunile inactive: se citesc
                                  împreună. Scria „Stinge", care nu spunea nici ce se apasă, nici ce
                                  se întâmplă (Alexandru, 22.09.2026). */}
                              Dezactivează
                            </button>
                          )}
                        </>
                      ) : (
                        canTexte && (
                          <button
                            onClick={() => activeaza.mutate(t.id)}
                            className="text-xs text-b4l-blue hover:underline"
                          >
                            Activează
                          </button>
                        )
                      )}
                      {/*
                        Cum cade textul in pagina, inainte sa fie al tuturor.

                        Alexandru: „pune la versiune un buton Vizualizare, cu care sa se vada
                        formatul". Pana acum, ca sa vezi cum arata trebuia s-o activezi si sa faci
                        o predare de proba — adica sa schimbi documentul semnat de toata lumea ca
                        sa afli daca arata bine.
                      */}
                      <button
                        onClick={() =>
                          void deschideFisier(
                            `/marketing/sampling/texte/${t.id}/previzualizare`,
                          ).catch((err: Error) => toast.error(err.message))
                        }
                        className="text-xs text-b4l-blue hover:underline"
                        title="Deschide un proces-verbal de model, cu textul ăsta pe pagina 2."
                      >
                        Vizualizare ↗
                      </button>
                    </div>
                    <p className="flex-1 whitespace-pre-wrap text-xs leading-relaxed text-b4l-text-muted">
                      {t.continut.length > 600 ? `${t.continut.slice(0, 600)}…` : t.continut}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )
      })}

      {adaugaTip && (
        <ModalTextNou
          tip={adaugaTip}
          onClose={() => setAdaugaTip(null)}
          onSave={(b) => adauga.mutate(b)}
          seSalveaza={adauga.isPending}
        />
      )}
    </div>
  )
}

function CostActiune({ id }: { id: number }) {
  const q = useQuery({
    queryKey: ['sampling', 'actiune', id, 'cost'],
    queryFn: async () =>
      (
        await api.get<{
          total: number
          marfa: number
          consumDirect: number
          facturi: number
          deconturi: number
        }>(`/marketing/sampling/actiuni/${id}/cost`)
      ).data,
  })
  const c = q.data
  if (!c || c.total === 0) return null
  const lei = (n: number) =>
    n.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const parti = [
    ['marfă predată', c.marfa],
    ['consum direct', c.consumDirect],
    ['facturi', c.facturi],
    ['deconturi', c.deconturi],
  ].filter(([, v]) => (v as number) > 0) as [string, number][]

  return (
    <div className="mb-3 rounded-lg border border-b4l-border p-3">
      <div className="flex items-baseline gap-2">
        <span className="text-sm font-medium text-b4l-text">Cost total</span>
        <span className="ml-auto tabular-nums text-b4l-text">{lei(c.total)} lei</span>
      </div>
      {parti.length > 0 && (
        <p className="mt-1 text-xs text-b4l-text-muted">
          {parti.map(([eticheta, v]) => `${eticheta} ${lei(v)}`).join(' · ')}
        </p>
      )}
    </div>
  )
}

function ModalTextNou({
  tip,
  onClose,
  onSave,
  seSalveaza,
}: {
  tip: 'informare' | 'mostre' | 'farmacie' | 'eveniment' | 'tombola' | 'promo' | 'marketing'
  onClose: () => void
  onSave: (b: Record<string, unknown>) => void
  seSalveaza: boolean
}) {
  const [versiune, setVersiune] = useState('')
  const [continut, setContinut] = useState('')
  const [activ, setActiv] = useState(true)

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      /* Titlul urmeaza tipul deschis. Scria „Tombolă — versiune nouă" si cand adaugai la
         farmacie, la eveniment sau la promoțional. */
      title={`${ETICHETA_SCURTA[tip] ?? tip} — versiune nouă`}
    >
      <div className="space-y-3">
        <Field
          label="Versiune"
          value={versiune}
          onChange={(e) => setVersiune(e.target.value)}
          hint="Ex. 1.0. Apare pe procesul-verbal, ca dovadă a ce s-a afișat la semnare."
        />
        <label className="block">
          <span className="text-sm font-medium text-b4l-text">Text</span>
          <textarea
            value={continut}
            onChange={(e) => setContinut(e.target.value)}
            rows={16}
            className="mt-1 w-full rounded-lg border border-b4l-border p-3 text-sm"
            placeholder="Textul integral, așa cum a fost validat juridic."
          />
          <span className="mt-1 block text-xs text-b4l-text-muted">
            {continut.trim().length} caractere (minim 50)
          </span>
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={activ} onChange={(e) => setActiv(e.target.checked)} />
          <span className="text-sm text-b4l-text">
            Activează acum (dezactivează versiunea curentă)
          </span>
        </label>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          Anulează
        </Button>
        <Button
          disabled={!versiune.trim() || continut.trim().length < 50 || seSalveaza}
          onClick={() =>
            onSave({ tip, versiune: versiune.trim(), continut: continut.trim(), activ })
          }
        >
          {seSalveaza ? 'Se salvează…' : 'Adaugă'}
        </Button>
      </div>
    </Modal>
  )
}

interface Setari {
  eaLocId: number
  modBonImplicit: string
  maxPerPersoana: number
  trimiteBonEa: boolean
  firmaNume: string
  firmaCif: string | null
  transferDoctype: string
  bonDoctype: string
  minuteIntreExtrageri: number
  /** De pe ce adresă pleacă mailurile către participanți. Gol = contul SMTP din Setări. */
  emailExpeditor: string | null
  randomOrgKeySet: boolean
  drawsSet: boolean
  comisieExtragere: string | null
  extrageriDeProba: boolean
  /** Există o parolă pusă pentru dezvăluirea contactelor? Parola în sine nu se întoarce. */
  parolaContacteSet: boolean
  /** Mesajele către participanți. `null` = textul implicit din cod. */
  emailPvSubiect: string | null
  emailPvCorp: string | null
  emailConfirmareSubiect: string | null
  emailConfirmareCorp: string | null
  emailAnulareSubiect: string | null
  emailAnulareCorp: string | null
  emailStergereSubiect: string | null
  emailStergereCorp: string | null
  urlPolitica: string | null
  categoriiMostre: string[]
}

/**
 * Setările modulului.
 *
 * Comutatorul de trimitere în EA pornește OPRIT și rămâne așa până când cineva se uită la un
 * bon în previzualizare și confirmă că arată bine. Un document de gestiune greșit se corectează
 * în contabilitate, nu aici.
 */
/**
 * Produsele care se pot da ca mostre — bifate direct aici.
 *
 * Selecția se făcea pe categoria produsului. Alexandru: „Categoria e un câmp care există și în
 * expert, o să se creeze confuzii". Are dreptate — categoria vine din ExpertAccounts și înseamnă
 * acolo altceva, iar cine ar fi schimbat-o în Expert ar fi scos produsul din sampling fără să
 * știe. Acum e un marcaj propriu, care înseamnă un singur lucru.
 *
 * Bifarea salvează imediat, fără buton separat: e o singură valoare booleană, iar un „Salvează"
 * care păzește o bifă e o ocazie în plus de a pleca fără să salvezi.
 */
function ProduseMostra({ canWrite }: { canWrite: boolean }) {
  const queryClient = useQueryClient()
  const [cauta, setCauta] = useState('')

  const catalog = useQuery({
    queryKey: ['sampling', 'catalog', cauta],
    queryFn: async () =>
      (
        await api.get<
          { id: number; sku: string; name: string; category: string; esteMostra: boolean }[]
        >('/marketing/sampling/catalog', { params: cauta.trim() ? { cauta: cauta.trim() } : {} })
      ).data,
  })

  const bifeaza = useMutation({
    mutationFn: async (v: { id: number; esteMostra: boolean }) =>
      api.patch(`/marketing/sampling/catalog/${v.id}`, { esteMostra: v.esteMostra }),
    onSuccess: () => {
      // Se invalidează tot modulul: listele de produse din acțiuni, kituri și transfer se
      // hrănesc din același marcaj.
      void queryClient.invalidateQueries({ queryKey: ['sampling'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  const categorii = useQuery({
    queryKey: ['sampling', 'catalog', 'categorii'],
    queryFn: async () =>
      (
        await api.get<{ categorie: string; total: number; bifate: number }[]>(
          '/marketing/sampling/catalog/categorii',
        )
      ).data,
  })

  /**
   * Bifarea unei categorii întregi.
   *
   * Una câte una merge pentru câteva produse. Pentru „Tipărituri" (15) sau „Ambalare" (24) e o
   * corvoadă care se face pe jumătate și se uită — de-aia erau 192 de produse în catalog și doar
   * 4 bifate, deși materialele de marketing existau de mult.
   */
  const bifeazaCategoria = useMutation({
    mutationFn: async (v: { categorie: string; esteMostra: boolean }) =>
      (await api.post<{ modificate: number }>('/marketing/sampling/catalog/categorie', v)).data,
    onSuccess: (r, v) => {
      toast.success(
        v.esteMostra
          ? `${r.modificate} produse din „${v.categorie}" se pot da la sampling`
          : `${r.modificate} produse scoase din „${v.categorie}"`,
      )
      void queryClient.invalidateQueries({ queryKey: ['sampling'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })
  const [categorie, setCategorie] = useState('')
  const catAleasa = categorii.data?.find((c) => c.categorie === categorie)

  const bifate = catalog.data?.filter((p) => p.esteMostra).length ?? 0

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-sm font-medium text-b4l-text">
          Produsele care se pot da la sampling
        </span>
        <span className="text-xs text-b4l-text-muted">{bifate} bifate</span>
      </div>
      <p className="mt-0.5 text-xs text-b4l-text-muted">
        Doar astea pot fi puse pe o acțiune, într-un kit sau pe un transfer. Bifa se salvează pe
        loc.
      </p>

      {canWrite && categorii.data && (
        <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg bg-b4l-bg p-2 text-xs">
          <span className="text-b4l-text-muted">Bifează o categorie întreagă:</span>
          <select
            value={categorie}
            onChange={(e) => setCategorie(e.target.value)}
            className="rounded border border-b4l-border px-2 py-1"
          >
            <option value="">Alege categoria…</option>
            {categorii.data.map((c) => (
              <option key={c.categorie} value={c.categorie}>
                {c.categorie} — {c.bifate}/{c.total} bifate
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={!catAleasa || bifeazaCategoria.isPending}
            onClick={() => bifeazaCategoria.mutate({ categorie, esteMostra: true })}
            className="rounded bg-b4l-green px-2 py-1 text-white disabled:opacity-40"
          >
            Bifează toate
          </button>
          {!!catAleasa?.bifate && (
            <button
              type="button"
              disabled={bifeazaCategoria.isPending}
              onClick={() => bifeazaCategoria.mutate({ categorie, esteMostra: false })}
              className="rounded border border-b4l-border px-2 py-1 text-b4l-text-muted"
            >
              Debifează toate
            </button>
          )}
        </div>
      )}

      <input
        value={cauta}
        onChange={(e) => setCauta(e.target.value)}
        placeholder="Caută după denumire sau SKU"
        className="mt-2 w-full rounded-lg border border-b4l-border px-3 py-2 text-sm"
      />

      {catalog.isLoading ? (
        <p className="mt-2 text-sm text-b4l-text-muted">Se încarcă…</p>
      ) : !catalog.data?.length ? (
        <p className="mt-2 text-sm text-b4l-text-muted">Niciun produs găsit.</p>
      ) : (
        <div className="mt-2 max-h-80 overflow-y-auto rounded-lg border border-b4l-border">
          {catalog.data.map((p) => (
            <label
              key={p.id}
              className="flex cursor-pointer items-center gap-2 border-b border-b4l-border px-3 py-1.5 text-sm last:border-0 hover:bg-b4l-bg"
            >
              <input
                type="checkbox"
                checked={p.esteMostra}
                disabled={!canWrite || bifeaza.isPending}
                onChange={(e) => bifeaza.mutate({ id: p.id, esteMostra: e.target.checked })}
                className="h-4 w-4"
              />
              <span className="w-20 shrink-0 font-mono text-xs text-b4l-text-dim">{p.sku}</span>
              <span className="flex-1 text-b4l-text">{p.name}</span>
              <span className="text-xs text-b4l-text-dim">{p.category}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Categoriile din care se pot alege produse la sampling.
 *
 * Bife peste categoriile care CHIAR există în catalog, cu numărul de articole al fiecăreia. Nu
 * un câmp liber: categoriile vin de la ExpertAccounts și se scriu exact cum apar acolo, iar
 * „Suplimente Alimentare" cu A mare nu s-ar potrivi cu nimic și n-ar spune nimănui de ce lista
 * a rămas goală.
 *
 * O categorie scoasă de aici nu ascunde produsele deja folosite: acelea rămân prin `esteMostra`,
 * care se pune singură la folosire. Cele două se adună, nu se exclud.
 */
function CategoriiMostre({
  alese,
  canWrite,
  onChange,
}: {
  alese: string[]
  canWrite: boolean
  onChange: (v: string[]) => void
}) {
  const catalog = useQuery({
    queryKey: ['sampling', 'categorii'],
    queryFn: async () =>
      (
        await api.get<{ categorii: { categorie: string; cate: number }[] }>(
          '/marketing/sampling/categorii',
        )
      ).data,
  })

  return (
    <div className="block">
      <span className="text-xs text-b4l-text-muted">
        Categoriile din care se pot alege produse la sampling
      </span>
      <div className="mt-1 max-h-48 overflow-y-auto rounded-lg border border-b4l-border p-2">
        {catalog.isLoading && <p className="text-xs text-b4l-text-muted">Se încarcă…</p>}
        {catalog.data?.categorii.map((c) => (
          <label key={c.categorie} className="flex items-center gap-2 py-1 text-sm">
            <input
              type="checkbox"
              checked={alese.includes(c.categorie)}
              disabled={!canWrite}
              onChange={(e) =>
                onChange(
                  e.target.checked
                    ? [...alese, c.categorie]
                    : alese.filter((x) => x !== c.categorie),
                )
              }
              className="h-4 w-4"
            />
            <span className="text-b4l-text">{c.categorie}</span>
            <span className="text-xs text-b4l-text-muted">({c.cate})</span>
          </label>
        ))}
      </div>
      <span className="mt-1 block text-xs text-b4l-text-muted">
        Produsele din categoriile bifate apar direct la acțiuni, kituri și transferuri. Restul apar
        după ce au fost folosite o dată — bifa de mostră se pune singură atunci. O categorie
        debifată nu ascunde ce s-a mai dat.
      </span>
    </div>
  )
}

function TabSetari({ canWrite }: { canWrite: boolean }) {
  const queryClient = useQueryClient()
  /*
    Parola datelor de contact: doar adminii o schimba.

    Alexandru: „parola pentru datele de contact vreau sa poata fi modificata doar de admini".
    Oprirea reala e in API; asta e doar ca oamenii de la stand sa nu vada un camp care oricum i-ar
    refuza. Un camp care nu functioneaza pentru tine e mai rau decat unul care nu e acolo.
  */
  const eAdmin = useAuthStore((s) => s.user?.role) === 'admin'
  const setari = useQuery({
    queryKey: ['sampling', 'setari'],
    queryFn: async () => (await api.get<Setari>('/marketing/sampling/setari')).data,
  })

  // Cheia se tine separat de restul setărilor: ea nu se întoarce niciodată de la server, deci
  // n-are cum să facă parte din obiectul editat. Gol la salvare = se păstrează cea existentă.
  const [cheieNoua, setCheieNoua] = useState('')
  const [drawsLogin, setDrawsLogin] = useState('')
  const [drawsParola, setDrawsParola] = useState('')
  const [rezultatDraws, setRezultatDraws] = useState<{ ok: boolean; mesaj: string } | null>(null)

  /*
    Proba contului de extrageri. Tine o extragere `test` la random.org — gratuita, fara inregistrare
    — deci se poate apasa oricat, inclusiv in dimineata evenimentului.
  */
  const testeazaDraws = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ ok: boolean; mesaj: string }>(
          '/marketing/sampling/setari/testeaza-draws',
          {
            ...(drawsLogin.trim() ? { login: drawsLogin.trim() } : {}),
            ...(drawsParola ? { parola: drawsParola } : {}),
          },
          { timeout: 40_000 },
        )
      ).data,
    onSuccess: (r) => setRezultatDraws(r),
    onError: (e) => setRezultatDraws({ ok: false, mesaj: mesaj(e) }),
  })

  /*
    Proba cheii random.org. Nu scrie nimic: cere un numar dintr-un interval de doua elemente, doar
    ca sa treaca pe calea semnata, si raporteaza ce a iesit.
  */
  const [rezultatCheie, setRezultatCheie] = useState<{
    ok: boolean
    /** Semnat nu merge, dar cheia lucreaza pe Basic API — galben, nu rosu. */
    partial?: boolean
    sursa: string | null
    mesaj: string
  } | null>(null)
  const testeazaCheia = useMutation({
    mutationFn: async () =>
      (
        await api.post<{
          ok: boolean
          partial?: boolean
          sursa: string | null
          mesaj: string
        }>(
          '/marketing/sampling/setari/testeaza-random',
          // Ce e scris in camp bate ce e salvat: altfel „lipesc si probez" testeaza cheia veche.
          cheieNoua.trim() ? { cheie: cheieNoua.trim() } : {},
          { timeout: 30_000 },
        )
      ).data,
    onSuccess: (r) => setRezultatCheie(r),
    onError: (e) => setRezultatCheie({ ok: false, sursa: null, mesaj: mesaj(e) }),
  })
  const [parolaNoua, setParolaNoua] = useState('')

  const salveaza = useMutation({
    mutationFn: async (body: Partial<Setari> & { parolaContacte?: string | null }) =>
      api.patch('/marketing/sampling/setari', {
        ...body,
        ...(cheieNoua.trim() ? { randomOrgKey: cheieNoua.trim() } : {}),
        ...(drawsLogin.trim() ? { drawsLogin: drawsLogin.trim() } : {}),
        ...(drawsParola ? { drawsParola } : {}),
        ...(parolaNoua.trim() ? { parolaContacte: parolaNoua.trim() } : {}),
      }),
    onSuccess: () => {
      toast.success('Setări salvate')
      setCheieNoua('')
      setParolaNoua('')
      void queryClient.invalidateQueries({ queryKey: ['sampling', 'setari'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  const [local, setLocal] = useState<Setari | null>(null)
  const val = local ?? setari.data ?? null

  if (!val) return <Card>{<p className="p-4 text-sm text-b4l-text-muted">Se încarcă…</p>}</Card>

  const set = (p: Partial<Setari>) => setLocal({ ...val, ...p })

  return (
    <Card>
      <div className="space-y-4 p-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label="Gestiunea din EA (locid)"
            type="number"
            value={String(val.eaLocId)}
            onChange={(e) => set({ eaLocId: Number(e.target.value) || 0 })}
            disabled={!canWrite}
            hint="Gestiunea „Sampling” are id-ul 27. De aici ies mostrele pe bonul de consum."
          />
          <Field
            label="Firma de pe documentele EA (bill_name)"
            value={val.firmaNume}
            onChange={(e) => set({ firmaNume: e.target.value })}
            disabled={!canWrite}
            hint="Partenerul de pe bonul de consum și de pe transfer — noi înșine. Trebuie să se potrivească exact cu partenerul din EA, altfel documentul e respins."
          />
          <Field
            label="CIF-ul firmei (bill_regcode) — opțional"
            value={val.firmaCif ?? ''}
            onChange={(e) => set({ firmaCif: e.target.value || null })}
            disabled={!canWrite}
            hint="Dacă îl completezi, EA identifică firma după cod, nu după cum e scris numele. Mai sigur."
          />
          <Field
            label="Expeditorul e-mailurilor către participanți"
            value={val.emailExpeditor ?? ''}
            onChange={(e) => set({ emailExpeditor: e.target.value || null })}
            disabled={!canWrite}
            placeholder="Boost4Life <mkt@boost4life.ro>"
            hint="Adresa de expeditor a proceselor-verbale și a confirmărilor de înscriere; aici ajung și răspunsurile. Adaugă și un nume, ex.: „Boost4Life <mkt@boost4life.ro>”. Lasă gol ca să se folosească contul de e-mail din Setări."
          />
          {/*
            Parola care descuie datele de contact. Se pune de admini si se poate reseta.

            Nu se intoarce niciodata spre ecran — ca tokenii EA si cheia random.org — deci campul
            e mereu gol si spune doar daca exista una. Reset inseamna „nimeni nu mai vede
            datele pana nu punem alta": mai bine inchis pentru toti decat deschis din greseala.
          */}
          <div className="sm:col-span-2 rounded-lg border border-b4l-border p-3">
            <p className="text-sm font-medium text-b4l-text">Parola pentru datele de contact</p>
            <p className="mt-0.5 text-xs text-b4l-text-muted">
              În lista de contacte, telefonul și emailul apar mascate. Cine are nevoie de ele le
              cere pe rând, cu parola asta, iar cererea se scrie în jurnal cu numele lui.
              {val.parolaContacteSet
                ? ' ✓ este pusă'
                : ' ✗ nu e pusă — datele rămân mascate pentru toți'}
              {!eAdmin && ' Se schimbă doar de un administrator.'}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <input
                type="password"
                value={parolaNoua}
                onChange={(e) => setParolaNoua(e.target.value)}
                placeholder={
                  val.parolaContacteSet ? 'Parolă nouă (lasă gol ca să păstrezi)' : 'Parolă nouă'
                }
                disabled={!canWrite || !eAdmin}
                className="min-w-[14rem] flex-1 rounded-lg border border-b4l-border px-3 py-2 text-sm disabled:bg-b4l-bg"
              />
              {val.parolaContacteSet && canWrite && eAdmin && (
                <button
                  onClick={() => {
                    if (window.confirm('Ștergi parola? Datele rămân mascate pentru toți.')) {
                      // `null` = reset explicit. Nu face parte din `Setari` — parola nu se
                      // intoarce niciodata de la server, deci n-are ce cauta in obiectul editat.
                      salveaza.mutate({
                        ...(local ?? {}),
                        parolaContacte: null,
                      } as Partial<Setari> & {
                        parolaContacte: null
                      })
                    }
                  }}
                  className="text-xs text-b4l-red hover:underline"
                >
                  Resetează
                </button>
              )}
            </div>
          </div>
          {/*
            Mesajele catre participanti.

            Sunt doua, si spun lucruri diferite: unul insoteste procesul-verbal al unei predari,
            celalalt confirma o inscriere la tombola, unde nu s-a predat nimic. Amestecate intr-un
            singur sablon, unul dintre ele ar minti.

            Gol = textul implicit din cod, care ramane in vigoare. Nu se sterge nimic: cine goleste
            campul primeste inapoi ce era inainte.
          */}
          <div className="space-y-3 sm:col-span-2 rounded-lg border border-b4l-border p-3">
            <div>
              <p className="text-sm font-medium text-b4l-text">Mesajele către participanți</p>
              <p className="mt-0.5 text-xs text-b4l-text-muted">
                Poți folosi <code>{'{nrPv}'}</code>, <code>{'{actiune}'}</code>,{' '}
                <code>{'{firma}'}</code>, <code>{'{nume}'}</code>, <code>{'{prenume}'}</code>,{' '}
                <code>{'{produse}'}</code>. Ce nu se recunoaște rămâne scris ca atare. Lasă gol ca
                să revii la textul implicit.
              </p>
            </div>

            <label className="block">
              <span className="text-xs text-b4l-text-muted">
                Confirmare de înscriere la tombolă — subiect
              </span>
              <input
                value={val.emailConfirmareSubiect ?? ''}
                onChange={(e) => set({ emailConfirmareSubiect: e.target.value || null })}
                disabled={!canWrite}
                placeholder="Înscriere confirmată — {actiune}"
                className="mt-1 w-full rounded-lg border border-b4l-border px-3 py-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-xs text-b4l-text-muted">Confirmare de înscriere — mesaj</span>
              <textarea
                rows={5}
                value={val.emailConfirmareCorp ?? ''}
                onChange={(e) => set({ emailConfirmareCorp: e.target.value || null })}
                disabled={!canWrite}
                placeholder="Bună ziua, vă confirmăm înscrierea la tombola „{actiune}”…"
                className="mt-1 w-full rounded-lg border border-b4l-border px-3 py-2 text-sm"
              />
            </label>

            {/*
              Mesajul de la anulare. Gol INSEAMNA ca nu se trimite nimic — nu e o scapare.

              Un „ti-am anulat inscrierea", fara nimic altceva, naste mai multe intrebari decat
              raspunde. Mai bine tacere decat un mesaj care lasa omul sa se intrebe.
            */}
            <label className="block">
              <span className="text-xs text-b4l-text-muted">
                La anularea unei înscrieri — subiect
              </span>
              <input
                value={val.emailAnulareSubiect ?? ''}
                onChange={(e) => set({ emailAnulareSubiect: e.target.value || null })}
                disabled={!canWrite}
                placeholder="Actualizare privind înscrierea ta"
                className="mt-1 w-full rounded-lg border border-b4l-border px-3 py-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-xs text-b4l-text-muted">Mesajul de la anulare</span>
              <textarea
                rows={4}
                value={val.emailAnulareCorp ?? ''}
                onChange={(e) => set({ emailAnulareCorp: e.target.value || null })}
                disabled={!canWrite}
                placeholder="Lasă gol ca să nu se trimită nimic la anulare."
                className="mt-1 w-full rounded-lg border border-b4l-border px-3 py-2 text-sm"
              />
              <span className="mt-1 block text-xs text-b4l-text-muted">
                Aici merge și <code>{'{motiv}'}</code>. Cât timp câmpul e gol nu pleacă niciun mesaj
                — mai bine tăcere decât un anunț care lasă participantul cu întrebări.
              </span>
            </label>

            {/*
              Categoriile din care se pot alege produse la sampling.

              Se BIFEAZA, nu se tasteaza: vin de la EA si se scriu exact cum apar acolo. Un camp
              liber ar produce „Suplimente Alimentare" cu A mare, care nu se potriveste cu nimic
              si nu spune nimanui de ce lista a ramas goala.
            */}
            <CategoriiMostre
              alese={val.categoriiMostre ?? []}
              canWrite={canWrite}
              onChange={(categoriiMostre) => set({ categoriiMostre })}
            />

            {/*
              Politica generala de confidentialitate de pe site. In setari, nu in cod: se poate
              schimba oricand, iar un link mort langa o bifa obligatorie e mai rau decat niciun
              link.
            */}
            <label className="block">
              <span className="text-xs text-b4l-text-muted">
                Politica de confidențialitate (adresa de pe site)
              </span>
              <input
                value={val.urlPolitica ?? ''}
                onChange={(e) => set({ urlPolitica: e.target.value || null })}
                disabled={!canWrite}
                placeholder="https://www.boost4life.ro/pages/politica-de-confidentialitate"
                className="mt-1 w-full rounded-lg border border-b4l-border px-3 py-2 text-sm"
              />
              <span className="mt-1 block text-xs text-b4l-text-muted">
                Apare ca link sub bifele din formular. NU înlocuiește informarea campaniei — aceea e
                versionată și legată de fiecare semnătură, deci e dovada; politica e contextul.
              </span>
            </label>

            {/*
              Mesajul de la STERGEREA datelor, separat de cel de la anulare. Sunt situatii
              diferite, iar aici golul NU inseamna tacere: pleaca textul implicit din cod.
            */}
            <label className="block">
              <span className="text-xs text-b4l-text-muted">
                La ștergerea datelor la cerere — subiect
              </span>
              <input
                value={val.emailStergereSubiect ?? ''}
                onChange={(e) => set({ emailStergereSubiect: e.target.value || null })}
                disabled={!canWrite}
                placeholder="Datele tale au fost șterse"
                className="mt-1 w-full rounded-lg border border-b4l-border px-3 py-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-xs text-b4l-text-muted">Mesajul de la ștergerea datelor</span>
              <textarea
                rows={4}
                value={val.emailStergereCorp ?? ''}
                onChange={(e) => set({ emailStergereCorp: e.target.value || null })}
                disabled={!canWrite}
                placeholder="Lasă gol ca să se folosească textul implicit."
                className="mt-1 w-full rounded-lg border border-b4l-border px-3 py-2 text-sm"
              />
              <span className="mt-1 block text-xs text-b4l-text-muted">
                Spre deosebire de anulare, aici <strong>golul nu înseamnă tăcere</strong>: pleacă un
                text implicit. Art. 12 alin. (3) GDPR cere ca participantul să fie informat despre
                ce s-a făcut ca urmare a cererii lui, iar asta nu se poate lăsa pe seama unei setări
                necompletate. Mesajul pleacă <strong>înainte</strong> de ștergere; după, nu mai avem
                unde.
              </span>
            </label>

            <label className="block">
              <span className="text-xs text-b4l-text-muted">
                Mesajul care însoțește procesul-verbal — subiect
              </span>
              <input
                value={val.emailPvSubiect ?? ''}
                onChange={(e) => set({ emailPvSubiect: e.target.value || null })}
                disabled={!canWrite}
                placeholder="Proces-verbal {nrPv} — mostre gratuite {firma}"
                className="mt-1 w-full rounded-lg border border-b4l-border px-3 py-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-xs text-b4l-text-muted">
                Mesajul care însoțește procesul-verbal
              </span>
              <textarea
                rows={6}
                value={val.emailPvCorp ?? ''}
                onChange={(e) => set({ emailPvCorp: e.target.value || null })}
                disabled={!canWrite}
                placeholder="Bună ziua, atașat găsiți procesul-verbal {nrPv}…"
                className="mt-1 w-full rounded-lg border border-b4l-border px-3 py-2 text-sm"
              />
              <span className="mt-1 block text-xs text-b4l-text-muted">
                Documentul PDF atașat nu se schimbă de aici — el e document justificativ, iar forma
                lui trebuie să rămână aceeași cu ce s-a trimis deja.
              </span>
            </label>
          </div>

          <div className="sm:col-span-2">
            <ProduseMostra canWrite={canWrite} />
          </div>

          {/*
            Cheia se pastreaza criptata in baza, ca tokenii EA, si nu se mai intoarce niciodata
            spre ecran — de aceea campul e mereu gol si spune doar daca exista una salvata.
          */}
          <div className="sm:col-span-2">
            <PasswordField
              label={
                val.randomOrgKeySet
                  ? 'Cheie random.org nouă (lasă gol pentru a păstra)'
                  : 'Cheie API random.org'
              }
              value={cheieNoua}
              onChange={(e) => setCheieNoua(e.target.value)}
              disabled={!canWrite}
            />
            <p className="mt-1 text-xs text-b4l-text-muted">
              {val.randomOrgKeySet ? (
                <>
                  Extragerile sunt <strong>semnate</strong>: lângă fiecare câștigător apare
                  „verifică la random.org”, iar oricine poate confirma la ei că numărul a ieșit
                  atunci, pentru intervalul acela.
                </>
              ) : (
                <>
                  Fără cheie, extragerea e la fel de automată și numărul tot de la random.org vine —
                  dar nu e semnat, deci nu se poate dovedi nimănui. Cheia se cere de pe{' '}
                  <code>api.random.org</code>; pentru o tombolă cu premii e nevoie de una
                  comercială, nu de cea gratuită pentru dezvoltatori.
                </>
              )}
            </p>
            {/*
              PROBA cheii, aici, nu in ziua evenimentului.

              O cheie gresita nu se vede la salvare si nici la extragere: apelul semnat cade in
              tacere pe cel nesemnat, extragerea reuseste, castigatorul e corect. Lipseste doar
              semnatura — adica exact ce face extragerea verificabila de altcineva decat de noi.
              Pana acum singura cale de a afla era sa faci o extragere adevarata, care consuma un
              premiu din cele paisprezece.
            */}
            {canWrite && (
              <div className="mt-2">
                <Button
                  variant="secondary"
                  onClick={() => testeazaCheia.mutate()}
                  disabled={testeazaCheia.isPending}
                >
                  {testeazaCheia.isPending
                    ? 'Se testează…'
                    : cheieNoua.trim()
                      ? 'Testează cheia scrisă mai sus'
                      : 'Testează cheia salvată'}
                </Button>
                {rezultatCheie && (
                  <p
                    className={`mt-2 rounded-lg p-2 text-xs ${
                      rezultatCheie.ok
                        ? 'bg-b4l-green-light text-b4l-green-dark'
                        : rezultatCheie.partial
                          ? 'bg-b4l-amber-light text-b4l-amber-dark'
                          : 'bg-b4l-red-light text-b4l-red-dark'
                    }`}
                  >
                    {rezultatCheie.mesaj}
                    {rezultatCheie.sursa && ` (sursa: ${rezultatCheie.sursa})`}
                  </p>
                )}
              </div>
            )}
          </div>

          {/*
            CONTUL DE EXTRAGERI, separat de cheia de mai sus.

            Sunt doua produse diferite la aceeasi firma. Cheia de deasupra e pentru Core API —
            numere dintr-un interval. Aici e Third-Party Draw Service: predai lista de participanti
            si primesti o inregistrare permanenta, pe care participantul o deschide singur.

            Autentificarea se face cu LOGIN SI PAROLA de cont, nu cu cheie. De aceea cine cauta
            „API key pentru draws" nu gaseste nimic si crede ca serviciul n-are API.
          */}
          <div className="sm:col-span-2 rounded-lg border border-b4l-border p-3">
            <p className="text-sm font-medium text-b4l-text">
              Cont draws.random.org — extrageri cu înregistrare publică
            </p>
            <p className="mt-1 text-xs text-b4l-text-muted">
              Serviciul construit pentru tombole: random.org ține extragerea ca terț și păstrează
              înregistrarea cinci ani, la o adresă pe care câștigătorul o poate deschide singur.
              Costă $4,95 per extragere și se plătește din credit preîncărcat.{' '}
              {val.drawsSet ? (
                <strong className="text-b4l-green-dark">Configurat.</strong>
              ) : (
                <strong className="text-b4l-amber-dark">
                  Neconfigurat — extragerile se fac fără înregistrare verificabilă.
                </strong>
              )}
            </p>

            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className="text-xs text-b4l-text-muted">
                  Utilizator {val.drawsSet && '(lasă gol pentru a păstra)'}
                </span>
                <input
                  value={drawsLogin}
                  onChange={(e) => setDrawsLogin(e.target.value)}
                  disabled={!canWrite}
                  autoComplete="off"
                  className="mt-1 w-full rounded-lg border border-b4l-border px-3 py-2 text-sm"
                />
              </label>
              <PasswordField
                label={val.drawsSet ? 'Parolă nouă (lasă gol pentru a păstra)' : 'Parolă'}
                value={drawsParola}
                onChange={(e) => setDrawsParola(e.target.value)}
                disabled={!canWrite}
              />
            </div>

            <label className="mt-3 block">
              <span className="text-xs text-b4l-text-muted">
                Comisia de extragere (câte un nume pe rând)
              </span>
              <textarea
                value={val.comisieExtragere ?? ''}
                onChange={(e) => set({ comisieExtragere: e.target.value || null })}
                disabled={!canWrite}
                rows={2}
                placeholder={`Dorian Ion
Adela Safta`}
                className="mt-1 w-full rounded-lg border border-b4l-border px-3 py-2 text-sm"
              />
              <span className="mt-1 block text-xs text-b4l-text-muted">
                Art. 4.6 din regulament cere ca procesul-verbal al fiecărei extrageri să cuprindă
                membrii comisiei. Se scriu o dată și apar pe fiecare document.
              </span>
            </label>

            {/*
              COMUTATORUL DE PROBA. Alexandru: „daca testam extragere cu inscrierile de test, va
              consuma din credit?". Da — $4,95 de fiecare data.

              Cu el pornit, extragerea se tine cu adevarat, aleatoriu, dar `recordType` e `test`:
              gratuita si fara urma in dosarul campaniei. Pericolul e sa ramana asa in ziua
              evenimentului, cand toate cele paisprezece ar iesi fara inregistrare — de aceea e
              scris cu galben si spune raspicat ce se pierde.
            */}
            <label className="mt-3 flex items-start gap-2 rounded-lg bg-b4l-amber-light p-3">
              <input
                type="checkbox"
                checked={val.extrageriDeProba}
                onChange={(e) => set({ extrageriDeProba: e.target.checked })}
                disabled={!canWrite}
                className="mt-0.5 h-5 w-5"
              />
              <span className="text-sm text-b4l-amber-dark">
                <strong>Extrageri de probă</strong> — gratuite, fără înregistrare la random.org.
                <span className="mt-1 block text-xs">
                  Pentru testat fluxul fără să consumi din credit. Extragerea e reală și aleatorie,
                  dar nu rămâne nicio dovadă publică. <strong>Stinge-l înainte de eveniment</strong>
                  , altfel toate extragerile ies fără înregistrare.
                </span>
              </span>
            </label>

            {canWrite && (
              <div className="mt-3">
                <Button
                  variant="secondary"
                  onClick={() => testeazaDraws.mutate()}
                  disabled={testeazaDraws.isPending}
                >
                  {testeazaDraws.isPending ? 'Se probează…' : 'Probează contul'}
                </Button>
                <span className="ml-2 text-xs text-b4l-text-muted">
                  Ține o extragere de test — gratuită, fără înregistrare și fără cost.
                </span>
                {rezultatDraws && (
                  <p
                    className={`mt-2 rounded-lg p-2 text-xs ${
                      rezultatDraws.ok
                        ? 'bg-b4l-green-light text-b4l-green-dark'
                        : 'bg-b4l-amber-light text-b4l-amber-dark'
                    }`}
                  >
                    {rezultatDraws.mesaj}
                  </p>
                )}
              </div>
            )}
          </div>

          {/*
            Pauza minima intre extrageri. Alexandru, dupa o extragere pornita prea repede: „e
            posibil sa fii apasat din greseala".
          */}
          <Field
            label="Pauză minimă între extrageri (minute)"
            type="number"
            value={String(val.minuteIntreExtrageri)}
            onChange={(e) =>
              set({ minuteIntreExtrageri: Math.max(0, Number(e.target.value) || 0) })
            }
            disabled={!canWrite}
            hint="0 = fără pauză. Se aplică doar extragerilor inițiale; reextragerea unui premiu neridicat nu e oprită niciodată (art. 6.3)."
          />

          {/*
            Tipul documentului bonului de consum. Alexandru: „as vrea sa pun bnc-mk in loc de bnc".

            Era scris fix in cod, desi e o setare de nomenclator din EA: firma poate avea serii
            separate pe departament, iar marketingul are alta serie decat depozitul. Camp liber, nu
            lista: seriile sunt ale firmei si n-avem de unde sa le stim pe toate.
          */}
          <label className="block">
            <span className="text-sm font-medium text-b4l-text">
              Tipul documentului de bon de consum
            </span>
            <input
              value={val.bonDoctype}
              onChange={(e) => set({ bonDoctype: e.target.value })}
              disabled={!canWrite}
              placeholder="bnc"
              className="mt-1 w-full rounded-lg border border-b4l-border px-3 py-2 font-mono text-sm"
            />
            <span className="mt-1 block text-xs text-b4l-text-muted">
              Seria din ExpertAccounts pe care pleacă bonurile de consum ale sampling-ului — de
              exemplu <code>bnc</code> sau <code>bnc-mk</code>. Trebuie să existe în nomenclatorul
              EA, altfel documentul e respins. Se aplică bonurilor emise de acum înainte; cele deja
              trimise rămân pe seria cu care au plecat.
            </span>
          </label>

          <label className="block">
            <span className="text-sm font-medium text-b4l-text">
              Tipul documentului de transfer
            </span>
            <select
              value={val.transferDoctype}
              onChange={(e) => set({ transferDoctype: e.target.value })}
              disabled={!canWrite}
              className="mt-1 w-full rounded-lg border border-b4l-border px-3 py-2 text-sm"
            >
              <option value="tc-out">tc-out — cu confirmare, primirea se face în EA</option>
              <option value="t-out">
                t-out — transfer direct, ieșirea și intrarea într-un document
              </option>
            </select>
            <span className="mt-1 block text-xs text-b4l-text-muted">
              Cu <code>tc-out</code>, primirea se face în EA („Transfer confirmat-Primire”), iar
              CRM-ul află singur, în cel mult 30 de minute. Documentul iese perfect: lot, batch,
              termen de valabilitate.
              <br />
              Cu <code>t-out</code>, ieșirea și intrarea sunt <strong>un singur document</strong>,
              cu același număr — la transferul manual `t325`, rândul de intrare a moștenit lotul
              285021 cu tot cu batch și expirare. Butonul „Primește” trimite atunci{' '}
              <code>t-in</code>
              cu același număr. În test.
            </span>
          </label>
          <Field
            label="Mostre per persoană, per acțiune"
            type="number"
            value={String(val.maxPerPersoana)}
            onChange={(e) => set({ maxPerPersoana: Math.max(0, Number(e.target.value) || 0) })}
            disabled={!canWrite}
            hint="0 = fără limită. Al doilea scan al aceleiași persoane primește un mesaj politicos."
          />
          <label className="block">
            <span className="text-sm font-medium text-b4l-text">
              Bon implicit pentru acțiuni noi
            </span>
            <select
              value={val.modBonImplicit}
              onChange={(e) => set({ modBonImplicit: e.target.value })}
              disabled={!canWrite}
              className="mt-1 w-full rounded-lg border border-b4l-border px-3 py-2 text-sm"
            >
              <option value="per_pv">{MOD_BON_LABEL.per_pv}</option>
              <option value="per_actiune">{MOD_BON_LABEL.per_actiune}</option>
            </select>
          </label>
        </div>

        <div className="rounded-lg border border-b4l-border p-3">
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={val.trimiteBonEa}
              onChange={(e) => set({ trimiteBonEa: e.target.checked })}
              disabled={!canWrite}
              className="mt-1 h-5 w-5"
            />
            <span className="text-sm text-b4l-text">
              Trimite bonurile de consum în ExpertAccounts
              <span className="mt-1 block text-xs text-b4l-text-muted">
                Cât timp e oprit, predările rămân cu bonul „Oprit” și poți vedea exact ce s-ar
                trimite, din butonul „Bon” de pe fiecare predare. Pornește-l după ce ai verificat o
                previzualizare și după ce tokenii pentru bonurile de consum sunt completați în
                Setări → EA.
              </span>
            </span>
          </label>
        </div>

        {canWrite && (
          <div className="flex justify-end">
            {/*
              Butonul nu se mai stinge. Un buton stins nu spune de ce e stins — arata a stricat.

              Victor: „nu ma lasa sa dau save", apoi „am pus asa si a functionat" dupa ce a
              schimbat un camp. Nimic nu era stricat: butonul astepta o modificare. Dar omul
              apasa si nu se intampla nimic, iar concluzia lui e ca aplicatia nu merge.

              Acum apesi oricand, iar daca n-ai schimbat nimic ti-o spune in loc sa taca.
            */}
            <Button
              disabled={salveaza.isPending}
              onClick={() => {
                /*
                  Si campurile contului de extrageri intra la socoteala. Alexandru a completat
                  utilizatorul si parola, a apasat Salveaza si i s-a raspuns „nu ai schimbat nimic"
                  — fiindca verificarea stia doar de cheia random.org si de parola de contacte.
                  Un mesaj care contrazice ce vede omul pe ecran e mai rau decat niciun mesaj.
                */
                if (
                  !local &&
                  !cheieNoua.trim() &&
                  !parolaNoua.trim() &&
                  !drawsLogin.trim() &&
                  !drawsParola
                ) {
                  toast.info('Nu ai schimbat nimic — nu e nimic de salvat.')
                  return
                }
                salveaza.mutate(local ?? {})
              }}
            >
              {salveaza.isPending ? 'Se salvează…' : 'Salvează'}
            </Button>
          </div>
        )}
      </div>
    </Card>
  )
}

interface Contact {
  id: number
  nume: string
  prenume: string
  email: string | null
  telefon: string | null
  functie: string | null
  sursa: string | null
  anonimizatLa: string | null
  nrPredari: number
  marketing: string[]
}

/**
 * Contactele adunate la sampling.
 *
 * Două acțiuni, deliberat separate: oprirea comunicărilor comerciale (reversibilă, se face
 * des) și ștergerea la cerere (ireversibilă, cere motiv). Puse la un loc sub un singur buton
 * „Șterge", cineva ar fi apăsat-o pe cea greșită.
 */
function TabContacte({
  canWrite,
  canGdpr,
  actiuni,
}: {
  canWrite: boolean
  canGdpr: boolean
  actiuni: Actiune[]
}) {
  const queryClient = useQueryClient()
  // Exportul scoate emailul si telefonul in clar, pentru toata baza. Doar adminii.
  const eAdmin = useAuthStore((s) => s.user?.role) === 'admin'
  const [q, setQ] = useState('')
  const [doarMarketing, setDoarMarketing] = useState(false)
  /*
    Ce s-a dezvaluit in sesiunea asta, si pentru cine. Nu se pastreaza nicaieri: la reincarcarea
    paginii datele sunt iar mascate. Cine are nevoie de ele le cere din nou — si atunci se scrie
    din nou in jurnal.
  */
  const [dezvaluite, setDezvaluite] = useState<
    Record<number, { email: string | null; telefon: string | null }>
  >({})
  const [deDezvaluit, setDeDezvaluit] = useState<Contact | null>(null)
  const [parola, setParola] = useState('')

  const dezvaluie = useMutation({
    mutationFn: async (v: { id: number; parola: string }) =>
      (
        await api.post<{ email: string | null; telefon: string | null }>(
          `/marketing/sampling/contacte/${v.id}/dezvaluie`,
          { parola: v.parola },
        )
      ).data,
    onSuccess: (d, v) => {
      setDezvaluite((x) => ({ ...x, [v.id]: d }))
      setDeDezvaluit(null)
      setParola('')
    },
    onError: (e) => toast.error(mesaj(e)),
  })
  const [tip, setTip] = useState<TipSampling>('toate')
  const [actiuneId, setActiuneId] = useState<number | null>(null)
  const [sursa, setSursa] = useState('toate')
  const [importOpen, setImportOpen] = useState(false)

  const surse = useQuery({
    queryKey: ['sampling', 'surse'],
    queryFn: async () => (await api.get<string[]>('/marketing/sampling/contacte/surse')).data,
  })
  const [tintaGdpr, setTintaGdpr] = useState<Contact | null>(null)

  const contacte = useQuery({
    queryKey: ['sampling', 'contacte', q, doarMarketing, tip, actiuneId, sursa],
    queryFn: async () =>
      (
        await api.get<Contact[]>('/marketing/sampling/contacte', {
          params: {
            q: q || undefined,
            doarMarketing: doarMarketing || undefined,
            tipActiune: tip === 'toate' ? undefined : tip,
            actiuneId: actiuneId ?? undefined,
            sursa: sursa === 'toate' ? undefined : sursa,
          },
        })
      ).data,
  })

  const revoca = useMutation({
    mutationFn: async (id: number) =>
      api.post(`/marketing/sampling/contacte/${id}/revoca-marketing`),
    onSuccess: () => {
      toast.success('Comunicările comerciale au fost oprite')
      void queryClient.invalidateQueries({ queryKey: ['sampling', 'contacte'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  return (
    <div className="space-y-3">
      <FiltreTip
        valoare={tip}
        onChange={(v) => {
          setTip(v)
          setActiuneId(null)
        }}
        actiuni={actiuni}
        actiuneId={actiuneId}
        onActiune={setActiuneId}
      />

      <div className="flex flex-wrap items-center gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Caută după nume, email sau telefon"
          className="w-72 rounded-lg border border-b4l-border px-3 py-2 text-sm"
        />
        <label className="flex items-center gap-2 text-sm text-b4l-text-muted">
          <input
            type="checkbox"
            checked={doarMarketing}
            onChange={(e) => setDoarMarketing(e.target.checked)}
          />
          Doar cei cu acord de marketing
        </label>

        <select
          value={sursa}
          onChange={(e) => setSursa(e.target.value)}
          className="rounded-lg border border-b4l-border bg-white px-2 py-1 text-sm"
        >
          <option value="toate">Toate sursele</option>
          {(surse.data ?? []).map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        <div className="ml-auto flex gap-2">
          {/*
            Exportul, doar la admini. Fisierul contine emailul si telefonul IN CLAR, pentru toata
            baza — adica exact ce apara parola datelor de contact din lista. Oprirea reala e pe
            ruta; aici doar ca sa nu apara un buton care raspunde cu 403.
          */}
          {eAdmin && (
            <Button
              variant="secondary"
              onClick={() => {
                void downloadFile(
                  '/marketing/sampling/contacte/export',
                  sursa === 'toate' ? {} : { sursa },
                  'contacte-sampling.xlsx',
                ).catch((e: Error) => toast.error(e.message))
              }}
            >
              Exportă
            </Button>
          )}
          {canWrite && <Button onClick={() => setImportOpen(true)}>Importă</Button>}
        </div>
      </div>

      <Card>
        {contacte.isLoading ? (
          <p className="p-4 text-sm text-b4l-text-muted">Se încarcă…</p>
        ) : !contacte.data?.length ? (
          <p className="p-4 text-sm text-b4l-text-muted">Niciun contact.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-b4l-text-dim">
                <tr>
                  <th className="px-3 py-2">Persoană</th>
                  <th className="px-3 py-2">Contact</th>
                  <th className="px-3 py-2">Ocupație</th>
                  <th className="px-3 py-2">Sursă</th>
                  <th className="px-3 py-2 text-right">Predări</th>
                  <th className="px-3 py-2">Marketing</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {contacte.data.map((c) => (
                  <tr key={c.id} className="border-t border-b4l-border">
                    <td className="px-3 py-2">
                      {c.prenume} {c.nume}
                      {c.anonimizatLa && (
                        <span className="ml-2 rounded-full bg-b4l-bg px-2 py-0.5 text-xs text-b4l-text-muted">
                          anonimizat
                        </span>
                      )}
                    </td>
                    {/*
                      Datele vin mascate de la server, nu ascunse pe ecran: ce nu pleaca de acolo
                      nu se citeste din inspectorul browserului si nu ajunge intr-o captura facuta
                      din greseala. Se cer pe rand, cu parola, si fiecare cerere intra in jurnal.
                    */}
                    <td className="px-3 py-2 text-b4l-text-muted">
                      {dezvaluite[c.id]?.email ?? c.email ?? '—'}
                      {(dezvaluite[c.id]?.telefon ?? c.telefon) && (
                        <div className="text-xs">{dezvaluite[c.id]?.telefon ?? c.telefon}</div>
                      )}
                      {!dezvaluite[c.id] && !c.anonimizatLa && (c.email || c.telefon) && (
                        <button
                          onClick={() => setDeDezvaluit(c)}
                          className="mt-0.5 block text-xs text-b4l-blue hover:underline"
                        >
                          Arată datele
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-2 text-b4l-text-muted">{c.functie ?? '—'}</td>
                    <td className="px-3 py-2 text-xs text-b4l-text-muted">{c.sursa ?? '—'}</td>
                    <td className="px-3 py-2 text-right">{c.nrPredari}</td>
                    <td className="px-3 py-2">
                      {c.marketing.length === 0 ? (
                        <span className="text-b4l-text-dim">—</span>
                      ) : (
                        <span className="text-xs text-b4l-green-dark">
                          {c.marketing
                            .map((m) => (m === 'marketing_email' ? 'email' : 'SMS'))
                            .join(', ')}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {canWrite && c.marketing.length > 0 && (
                        <button
                          onClick={() => revoca.mutate(c.id)}
                          className="text-xs text-b4l-text-muted hover:underline"
                        >
                          Oprește marketingul
                        </button>
                      )}
                      {canGdpr && !c.anonimizatLa && (
                        <button
                          onClick={() => setTintaGdpr(c)}
                          className="ml-3 text-xs text-b4l-red hover:underline"
                        >
                          Șterge datele
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {importOpen && <ModalImportContacte onClose={() => setImportOpen(false)} />}

      {deDezvaluit && (
        <Modal
          open
          onClose={() => {
            setDeDezvaluit(null)
            setParola('')
          }}
          title={`Datele lui ${deDezvaluit.prenume} ${deDezvaluit.nume}`.trim()}
        >
          <p className="text-sm text-b4l-text-muted">
            Parola o pun adminii, în Sampling → Setări. Cererea se scrie în jurnal, cu numele tău —
            parola apără datele de cine trece pe lângă ecran, jurnalul răspunde la întrebarea de
            după.
          </p>
          <input
            type="password"
            autoFocus
            value={parola}
            onChange={(e) => setParola(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && parola) {
                dezvaluie.mutate({ id: deDezvaluit.id, parola })
              }
            }}
            placeholder="Parola"
            className="mt-3 w-full rounded-lg border border-b4l-border px-3 py-2 text-sm"
          />
          <div className="mt-4 flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setDeDezvaluit(null)
                setParola('')
              }}
            >
              Anulează
            </Button>
            <Button
              disabled={!parola || dezvaluie.isPending}
              onClick={() => dezvaluie.mutate({ id: deDezvaluit.id, parola })}
            >
              {dezvaluie.isPending ? 'Se verifică…' : 'Arată'}
            </Button>
          </div>
        </Modal>
      )}

      {tintaGdpr && <ModalAnonimizare contact={tintaGdpr} onClose={() => setTintaGdpr(null)} />}
    </div>
  )
}

function ModalAnonimizare({ contact, onClose }: { contact: Contact; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [motiv, setMotiv] = useState('')

  const anonimizeaza = useMutation({
    mutationFn: async () =>
      api.post(`/marketing/sampling/contacte/${contact.id}/anonimizeaza`, { motiv: motiv.trim() }),
    onSuccess: () => {
      toast.success('Datele au fost șterse')
      onClose()
      void queryClient.invalidateQueries({ queryKey: ['sampling'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  return (
    <Modal open onClose={onClose} title="Ștergerea datelor la cerere">
      <div className="space-y-3">
        <p className="text-sm text-b4l-text">
          Se șterg numele, emailul, telefonul și ocupația pentru{' '}
          <strong>
            {contact.prenume} {contact.nume}
          </strong>
          . Acțiunea nu poate fi anulată.
        </p>
        <div className="rounded-lg bg-b4l-amber-light p-3 text-sm text-b4l-amber-dark">
          Cele {contact.nrPredari} procese-verbale rămân — sunt documente justificative și legea
          cere să le păstrăm 10 ani. Documentele deja emise, cu numele pe ele, rămân în arhivă; un
          proces-verbal regenerat de aici înainte va arăta „Date șterse la cerere”.
        </div>
        <Field
          label="Motivul cererii"
          value={motiv}
          onChange={(e) => setMotiv(e.target.value)}
          hint="Rămâne în jurnalul de audit. Ex.: „cerere prin email, 19.08.2026”."
        />
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          Anulează
        </Button>
        <Button
          disabled={motiv.trim().length < 3 || anonimizeaza.isPending}
          onClick={() => anonimizeaza.mutate()}
        >
          {anonimizeaza.isPending ? 'Se șterge…' : 'Șterge datele'}
        </Button>
      </div>
    </Modal>
  )
}

/**
 * Secțiune pliată, care se deschide cu un clic pe capul ei.
 *
 * Alexandru: „pagina a devenit greu de gestionat, din cauza că s-a lungit prea mult (...) oare am
 * putea face lista cu articole să fie implicit ascunsă?".
 *
 * Ferestrele de detaliu au crescut o dată cu modulul: șase câmpuri de configurare, plus catalogul
 * întreg de produse cu o bifă pe fiecare. Nimeni nu le folosește pe toate deodată, dar toate
 * stăteau deschise, iar ce cauți ajunge la al treilea ecran de derulat.
 *
 * Închisă implicit, DAR cu numărul scris pe cap: cine deschide fereastra vede din prima câte
 * produse are acțiunea, fără să desfacă nimic. O secțiune închisă care nu spune nimic despre ce
 * ascunde e mai rea decât una deschisă.
 *
 * Conținutul se demontează când se închide. Câmpurile se salvează la ieșirea din ele (`onBlur`),
 * iar bifele stau în starea ferestrei, nu în listă — deci nu se pierde nimic la închidere.
 */
const STARI_EMAIL: { v: string; t: string }[] = [
  { v: 'toate', t: 'Emailul — toate' },
  { v: 'trimis', t: 'Email trimis' },
  { v: 'esuat', t: 'Email eșuat' },
  { v: 'nou', t: 'Email netrimis' },
]

const STARI_BON: { v: string; t: string }[] = [
  { v: 'toate', t: 'Bonul EA — toate' },
  { v: 'trimis', t: 'Bon trimis' },
  { v: 'esuat', t: 'Bon eșuat' },
  { v: 'nou', t: 'Bon netrimis' },
  { v: 'oprit', t: 'Bon oprit din setări' },
  { v: 'nu_se_aplica', t: 'Fără bon (colectare)' },
]

/**
 * Filtrarea predărilor după starea emailului și a bonului, plus căutare liberă.
 *
 * Partea de server exista de mult — ruta acceptă `statusEmail`, `statusBon` și `q`, toate
 * validate —, dar interfața nu le-a trimis niciodată. Alexandru, 29.09.2026: „de adăugat
 * filtrare după status email, bon ea".
 *
 * Căutarea vine la pachet fiindcă e același parametru: caută în numărul PV, nume, prenume și
 * email. Fără ea, ca să găsești predarea unui om derulezi.
 */
function FiltrePredari({
  statusEmail,
  onStatusEmail,
  statusBon,
  onStatusBon,
  cauta,
  onCauta,
  gasite,
}: {
  statusEmail: string
  onStatusEmail: (v: string) => void
  statusBon: string
  onStatusBon: (v: string) => void
  cauta: string
  onCauta: (v: string) => void
  gasite: number
}) {
  const filtrat = statusEmail !== 'toate' || statusBon !== 'toate' || cauta.trim() !== ''
  const sel = 'rounded-lg border border-b4l-border bg-white px-2 py-1 text-sm'

  return (
    <div className="flex flex-wrap items-center gap-2">
      <input
        value={cauta}
        onChange={(e) => onCauta(e.target.value)}
        placeholder="Număr PV, nume sau email…"
        className="w-64 rounded-lg border border-b4l-border px-2 py-1 text-sm"
      />
      <select value={statusEmail} onChange={(e) => onStatusEmail(e.target.value)} className={sel}>
        {STARI_EMAIL.map((s) => (
          <option key={s.v} value={s.v}>
            {s.t}
          </option>
        ))}
      </select>
      <select value={statusBon} onChange={(e) => onStatusBon(e.target.value)} className={sel}>
        {STARI_BON.map((s) => (
          <option key={s.v} value={s.v}>
            {s.t}
          </option>
        ))}
      </select>

      {/* Câte au rămas, și cum se dă filtrul jos. O listă scurtă filtrată arată ca o listă goală. */}
      {filtrat && (
        <>
          <span className="text-xs text-b4l-text-muted">
            {gasite === 0 ? 'nicio predare pe filtrele astea' : `${gasite} predări`}
          </span>
          <button
            onClick={() => {
              onStatusEmail('toate')
              onStatusBon('toate')
              onCauta('')
            }}
            className="text-xs text-b4l-blue-dark hover:underline"
          >
            Renunță la filtre
          </button>
        </>
      )}
    </div>
  )
}

function Pliant({
  titlu,
  nota,
  deschisImplicit = false,
  children,
}: {
  titlu: string
  /** Ce se vede pe cap fără să deschizi — de regulă un număr. */
  nota?: string
  deschisImplicit?: boolean
  children: React.ReactNode
}) {
  const [deschis, setDeschis] = useState(deschisImplicit)
  return (
    <div className="rounded-lg border border-b4l-border">
      <button
        onClick={() => setDeschis((d) => !d)}
        /*
          Bara are fond, nu doar chenar. Alexandru: „colorează cu un gri mai intens bara care
          deschide aceste ecrane". Pe alb pe alb, capul nu se citea ca buton — arăta ca un titlu
          care se întâmplă să aibă o săgeată în dreapta, iar cine nu știa nu-l apăsa.

          `rounded-t-lg` singur cât e închisă, ca fondul să nu iasă din colțurile chenarului.
        */
        className={`flex w-full items-center justify-between gap-2 bg-b4l-bg-tare px-3 py-2 text-left hover:bg-b4l-bg-tare-hover ${
          deschis ? 'rounded-t-lg' : 'rounded-lg'
        }`}
        aria-expanded={deschis}
      >
        <span className="text-sm font-medium text-b4l-text">
          {titlu}
          {nota && <span className="ml-2 text-xs font-normal text-b4l-text-muted">{nota}</span>}
        </span>
        <ChevronDown
          size={16}
          className={`shrink-0 text-b4l-text-muted transition-transform ${
            deschis ? 'rotate-180' : ''
          }`}
        />
      </button>
      {deschis && <div className="border-t border-b4l-border px-3 py-3">{children}</div>}
    </div>
  )
}
